import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Constants, getRunningTimeMinutes, isUKCConformationUnscheduledLevel, countsTowardUKCBreedTile, isUKCConformationBISTile, isUkcRallyShow, ukcRallyClassLabel } from "@eagerdog/constants";
import { ceilToFiveMinuteInterval, extractJudgeScheduleFromRings, ringStartOffsetMinutes } from "@eagerdog/judge-schedule-util";
import {
  IEvent,
  IShow,
  IRunningOrder,
  IRunningOrderDogClass,
  IShowRing,
  isOfferedShowClass,
  RunningOrderDogClassDto
} from "@eagerdog/interfaces";
import moment from "moment";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { helperService } from "src/services/helper.service";
import { apiService } from "src/services";
import ContextMenu/*, { IContextMenuOption }*/ from "src/components/ContextMenu/ContextMenu";
import { Modal, useModal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Checkbox from "src/components/Checkbox/Checkbox";
import { toast } from "src/components/Toast/ToastManager";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import SelectJudges, { ISelectedJudge } from "./SelectJudges/SelectJudges";
import SelectRing from "./SelectRing/SelectRing";
import { EditRunningOrderDogClass, IEditRunningOrderDogClassValues } from "./EditRunningOrderDogClass";
import { AddRunningOrderJudge } from "./AddRunningOrderJudge";
import { MoveClassToRing } from "./MoveClassToRing";
import { useAKCAgilitySchedule } from "./hooks/useAKCAgilitySchedule";
import { useUKCConformationSchedule } from "./hooks/useUKCConformationSchedule";
import styles from "./JudgeSchedulesTab.module.scss";

// Simple debounce utility with flush/cancel support
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout | null = null;
  let lastArgs: any[] | null = null;

  const executedFunction: any = (...args: any[]) => {
    lastArgs = args;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      lastArgs = null;
      func(...args);
    }, wait);
  };

  executedFunction.flush = () => {
    if (timeout !== null && lastArgs !== null) {
      clearTimeout(timeout);
      timeout = null;
      const args = lastArgs;
      lastArgs = null;
      func(...args);
    }
  };

  executedFunction.cancel = () => {
    if (timeout !== null) clearTimeout(timeout);
    timeout = null;
    lastArgs = null;
  };

  return executedFunction;
};

// Module-level map that survives component unmount/remount cycles.
// When a save is initiated during unmount (flush on cleanup), the Promise is stored
// here so the next mount's loading effect can await it before fetching fresh DB data,
// eliminating the race between the outgoing flush-save and the incoming fresh-load.
const pendingShowSaves = new Map<string, Promise<any>>();

// AKC Conformation Specialty - Class order for sex split
// Order: Sweepstakes → Regular → Veteran → Best of Breed → Junior Showmanship
const AKC_SPECIALTY_SWEEPSTAKES_LEVELS = [
  Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
  Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
  Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
  Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
  Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
  Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
  Constants.dog_class_element_level.akc_puppy_sweepstakes,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
  Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus,
  Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
  Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
  Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y,
  Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus,
];

const AKC_SPECIALTY_REGULAR_LEVELS = [
  Constants.dog_class_element_level.akc_4_6_month_puppy,
  Constants.dog_class_element_level.akc_4_6_month_puppy_exhibitor_only,
  Constants.dog_class_element_level.akc_6_9_month_puppy,
  Constants.dog_class_element_level.akc_9_12_month_puppy,
  Constants.dog_class_element_level.akc_12_18_month_puppy,
  Constants.dog_class_element_level.akc_18_24_month_puppy,
  Constants.dog_class_element_level.akc_novice,
  Constants.dog_class_element_level.akc_amateur_owner_handler,
  Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
  Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
  Constants.dog_class_element_level.akc_american_bred,
  Constants.dog_class_element_level.akc_open,
  Constants.dog_class_element_level.akc_miscellaneous,
];

const AKC_SPECIALTY_VETERAN_LEVELS = [
  Constants.dog_class_element_level.akc_veteran_6_8_years,
  Constants.dog_class_element_level.akc_veteran_6_plus_years,
  Constants.dog_class_element_level.akc_veteran_7_plus_years,
  Constants.dog_class_element_level.akc_veteran_7_9_years,
  Constants.dog_class_element_level.akc_veteran_7_10_years,
  Constants.dog_class_element_level.akc_veteran_8_10_years,
  Constants.dog_class_element_level.akc_veteran_8_11_years,
  Constants.dog_class_element_level.akc_veteran_8_plus_years,
  Constants.dog_class_element_level.akc_veteran_9_plus_years,
  Constants.dog_class_element_level.akc_veteran_10_plus_years,
  Constants.dog_class_element_level.akc_veteran_10_12_years,
  Constants.dog_class_element_level.akc_veteran_11_plus_years,
  Constants.dog_class_element_level.akc_veteran_12_plus_years,
  Constants.dog_class_element_level.akc_veteran_13_plus_years,
];

// Classes that should NOT be split by sex.
// Matches the catalog: only Regular, Veteran, Sweepstakes, Hunting, and Field Trial
// classes are split Dogs/Bitches. Everything else renders as a combined class.
const AKC_SPECIALTY_NO_SPLIT_LEVELS = [
  Constants.dog_class_element_level.akc_best_of_breed,
  // Junior Showmanship (all levels)
  Constants.dog_class_element_level.akc_junior_showmanship,
  Constants.dog_class_element_level.akc_novice_junior,
  Constants.dog_class_element_level.akc_novice_intermediate,
  Constants.dog_class_element_level.akc_novice_senior,
  Constants.dog_class_element_level.akc_open_junior,
  Constants.dog_class_element_level.akc_open_intermediate,
  Constants.dog_class_element_level.akc_open_senior,
  Constants.dog_class_element_level.akc_master_class,
  // Specialty classes rendered as combined in the catalog (not split by sex)
  Constants.dog_class_element_level.akc_brace,
  Constants.dog_class_element_level.akc_brood_bitch,
  Constants.dog_class_element_level.akc_stud_dog,
  Constants.dog_class_element_level.akc_coursing,
  Constants.dog_class_element_level.akc_sled_dog,
  Constants.dog_class_element_level.akc_teams,
  Constants.dog_class_element_level.akc_parade_of_titleholders,
  Constants.dog_class_element_level.akc_futurity,
  Constants.dog_class_element_level.akc_best_head,
  Constants.dog_class_element_level.akc_best_movement,
  Constants.dog_class_element_level.akc_best_field,
];

// Helper to check if a class should be split by sex
const shouldSplitBySex = (level: string): boolean => {
  return !AKC_SPECIALTY_NO_SPLIT_LEVELS.some(
    noSplitLevel => level.toLowerCase() === noSplitLevel.toLowerCase()
  );
};

// Helper to get class order index for sorting
const getSpecialtyClassOrderIndex = (level: string): number => {
  // Check sweepstakes first
  const sweepIdx = AKC_SPECIALTY_SWEEPSTAKES_LEVELS.findIndex(
    l => level.toLowerCase() === l.toLowerCase()
  );
  if (sweepIdx !== -1) return sweepIdx;
  
  // Then regular classes
  const regularIdx = AKC_SPECIALTY_REGULAR_LEVELS.findIndex(
    l => level.toLowerCase() === l.toLowerCase()
  );
  if (regularIdx !== -1) return 100 + regularIdx;
  
  // Then veteran classes
  const vetIdx = AKC_SPECIALTY_VETERAN_LEVELS.findIndex(
    l => level.toLowerCase() === l.toLowerCase()
  );
  if (vetIdx !== -1) return 200 + vetIdx;
  
  // Best of Breed
  if (level.toLowerCase() === Constants.dog_class_element_level.akc_best_of_breed.toLowerCase()) {
    return 300;
  }
  
  // Junior Showmanship at the end
  const jsIdx = AKC_SPECIALTY_NO_SPLIT_LEVELS.findIndex(
    l => level.toLowerCase() === l.toLowerCase()
  );
  if (jsIdx !== -1) return 400 + jsIdx;
  
  return 999;
};

function isScentworkClassOffered(
  show: IShow,
  dogClass: { show_element?: string; level?: string; section?: string; type?: string }
): boolean {
  if (show.show_type !== Constants.show_type.scentwork) {
    return true;
  }
  return isOfferedShowClass(show.show_elements, dogClass);
}

interface IProps {
  event: IEvent,
  show: IShow,
  onUpdate(updatedShow: IShow): void,
  onJudgeScheduleChange?(): void,
  onShowTitleChange?(titleData: { mainTitle: string; subTitle?: string }): void,
  onRegisterReload?(fn: () => void): void,
}

interface IRunningOrderDogClassDrag extends IRunningOrderDogClass {
  index: number
  label?: string
  group_order?: number
  sex_split?: "Dogs" | "Bitches"
  variation?: string
  split_number?: number
  split_total?: number
}

interface IRunningOrderDrag extends IRunningOrder {
  total_hours: number,
  dog_classes: IRunningOrderDogClassDrag[]
}

interface IEditingJudge {
  index: number;
  judge_name: string;
  judge_number?: string;
}

const JudgeSchedulesTab: React.FC<IProps> = (props) => {
  // Destructure props to avoid dependency issues
  const { event, show: propShow, onUpdate, onJudgeScheduleChange, onShowTitleChange, onRegisterReload } = props;
  

  const { isShown, toggle } = useModal();

  const [show, setShow] = useState<IShow>(propShow);
  const [loaded, setLoaded] = useState<boolean>(false);
  // Register a reload function so RunOrderTab (via ManageShow) can trigger a fresh
  // load of the judge schedule after saving dog run-order changes to the DB.
  useEffect(() => {
    if (onRegisterReload) {
      onRegisterReload(() => setLoaded(false));
    }
  // onRegisterReload is stable (passed from ManageShow's inline arrow, captured once)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [allShows, setAllShows] = useState<IShow[]>([]);
  const [allScores, setAllScores] = useState<any[]>([]);
  const [ringName, setRingName] = useState<IOption>(propShow.rings?.length ? { id: propShow.rings[0].ring, value: propShow.rings[0].ring } : { id: Constants.ring_names.ring_1, value: Constants.ring_names.ring_1 });

  const [modalContent, setModalContent] = useState<string>();
  const [movingJudge, setMovingJudge] = useState<number>(-1);
  const [editingJudge, setEditingJudge] = useState<IEditingJudge | null>(null);
  const [movingDogClass, setMovingDogClass] = useState<IRunningOrderDogClass | null>(null);
  const [movingDogClassSourceJudge, setMovingDogClassSourceJudge] = useState<string>("");
  
  // New state for grouping functionality
  const [groupingMode, setGroupingMode] = useState<boolean>(false);
  const [selectedForGrouping, setSelectedForGrouping] = useState<string[]>([]);
  const [levelGroups, setLevelGroups] = useState<Map<string, string>>(new Map()); // Maps child level to parent level

  const [selectedDogClass, setSelectedDogClass] = useState<IRunningOrderDogClass | undefined>(undefined);
  const [selectedJudgeName, setSelectedJudgeName] = useState<string | undefined>(undefined);

  // Split state
  const [mergingDogClass, setMergingDogClass] = useState<{ se: IRunningOrderDogClassDrag; jIndex: number; seIndex: number } | null>(null);

  // State for hiding empty elements/levels
  const [hideEmptyClasses, setHideEmptyClasses] = useState<boolean>(true);

  const [ringOptions, setRingOptions] = useState<IOption[]>(propShow.rings?.map((r: IShowRing, index: number) => {
    return { id: r.ring, value: r.ring };
  }) ?? [
    { id: Constants.ring_names.ring_1, value: Constants.ring_names.ring_1 }
  ]);

  // Track the previous score count to prevent infinite loops
  const previousScoreCountRef = useRef<number>(0);
  // Guard against stale async running-order loads overwriting fresh data.
  const runningOrderLoadVersionRef = useRef<number>(0);
  // Always-fresh ref to `show` so stale debounce closures never use old ring data.
  const showRef = useRef<IShow>(show);
  useEffect(() => { showRef.current = show; }, [show]);

  // Keep ringOptions in sync with propShow.rings.
  // When Edit Show saves (adding/changing rings/judges), we must update ringOptions so the
  // ring appears in the dropdown. Trigger a reload whenever ring structure changes so the
  // fresh running-order is fetched from the DB.
  const lastRingsFingerprintRef = useRef<string>('');
  useEffect(() => {
    const incoming = propShow.rings?.map((r: IShowRing) => ({ id: r.ring, value: r.ring })) ??
      [{ id: Constants.ring_names.ring_1, value: Constants.ring_names.ring_1 }];
    // Fingerprint includes ring names AND judges so any change (new ring OR judge added via
    // Edit Show) triggers a reload — even when ring count stays the same.
    const fingerprint = (propShow.rings ?? []).map((r: IShowRing) => {
      const judgeKeys = (r.running_order ?? []).map((ro: any) => `${ro.judge_name}|${ro.judge_number || ''}`).join(';');
      return `${r.ring}[${judgeKeys}]`;
    }).join(',');
    if (fingerprint !== lastRingsFingerprintRef.current) {
      lastRingsFingerprintRef.current = fingerprint;
      setRingOptions(incoming);
      // Reset UKC Conformation init ref so the bootstrap logic re-runs after reload.
      // Without this, if Ring 1 was bootstrapped but never saved, switching away and back
      // would reload DB data (still only "Multiple Judges") and skip re-initialization
      // because the ref remained true.
      ukcConformationInitializedRef.current = false;
      // A reload fetches fresh show structure from DB so internal state stays current.
      setLoaded(false);
    }
  // ringOptions intentionally omitted — we only want to react to prop changes,
  // not create a loop triggered by our own setRingOptions call.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propShow.rings]);

  const [judges, setJudges] = useState<IRunningOrderDrag[]>([]);

  // Always-fresh ref to `judges` so commitJudgesToShow and handleRingChange
  // always read the latest value regardless of React render timing.
  const judgesRef = useRef<IRunningOrderDrag[]>([]);
  useEffect(() => { judgesRef.current = judges; }, [judges]);
  // Ring whose running order is currently held in `judges` (null while a ring switch loads).
  // Ring-scoped logic must not treat another ring's judges as the current ring's.
  const judgesRingIdRef = useRef<string | null>(null);
  // Ring currently on screen, read when a debounced save is queued so the save targets
  // the ring it was made in even if the user switches rings before it fires.
  const currentRingIdRef = useRef<string>(ringName.id);
  currentRingIdRef.current = ringName.id;
  const [hasUnsavedBreakChanges, setHasUnsavedBreakChanges] = useState<boolean>(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [akcConformationCounts, setAkcConformationCounts] = useState<Map<string, any>>(new Map());
  const [akcSpecialtyCounts, setAkcSpecialtyCounts] = useState<Map<string, any>>(new Map());
  const ukcConformationInitializedRef = useRef<boolean>(false); // Track if UKC Conformation has been initialized
  const akcAgilityInitializedRef = useRef<boolean>(false); // Track if AKC Agility has been initialized
  const akcSpecialtySexSplitInitializedRef = useRef<boolean>(false); // Track if AKC specialty sex split has been applied

  // Fetch AKC Agility class data
  const akcAgilityData = useAKCAgilitySchedule(event, propShow);

  // UKC Conformation-specific schedule logic (counts, tile distribution, element sync)
  const { ukcConformationCounts, ukcConformationCountsLoaded, buildInitialJudges: buildUKCConformationJudges, syncElementTiles: syncUKCElementTiles } =
    useUKCConformationSchedule(event, propShow, allScores);
  
  // Helper to detect if this is a specialty show
  // True for UKC Conformation shows — used in several places to special-case breed group tile handling.
  const isUKCConformationShow =
    event.sanctioning_club === Constants.sanctioning_club.UKC &&
    propShow.show_type === Constants.show_type.conformation;

  const isSpecialtyShow = useMemo(() => {
    return event.sanctioning_club === Constants.sanctioning_club.AKC && 
           propShow.show_type === Constants.show_type.conformation &&
           propShow.specialty_breed && 
           propShow.specialty_breed.trim() !== "";
  }, [event.sanctioning_club, propShow.show_type, propShow.specialty_breed]);
  
  // Reset UKC Conformation initialization when ring changes
  useEffect(() => {
    ukcConformationInitializedRef.current = false;
    akcAgilityInitializedRef.current = false;
    akcSpecialtySexSplitInitializedRef.current = false;
  }, [ringName.id]);

  // Sync propShow changes to local show state (e.g., when RunOrderTab updates)
  // Don't force reload - let the existing loading logic handle it
  useEffect(() => {
    setShow(propShow);
  }, [propShow]);


  // Fetch AKC Conformation counts if applicable
  useEffect(() => {
    if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
        propShow.show_type === Constants.show_type.conformation) {
      
      // For specialty shows, use the specialty counts API
      if (isSpecialtyShow) {
        apiService.getAKCConformationSpecialtyCounts(event._id, propShow._id).then((response) => {
          
          // Handle new response format with counts (variationCounts are consumed server-side)
          const counts = response?.counts || response;
          
          // Check if counts is valid and is an array
          if (!counts || !Array.isArray(counts)) {
            console.warn('AKC specialty counts response is not valid:', counts);
            return;
          }
          
          const countsMap = new Map();
          counts.forEach((count: any) => {
            // For specialty shows, key is "judgeName-classLevel"
            if (!count || !count.judge_name || !count.class_level) {
              console.warn('Invalid specialty count object:', count);
              return;
            }
            
            const key = `${count.judge_name}-${count.class_level}`;
            countsMap.set(key, { 
              males: count.male_count || 0,
              females: count.female_count || 0,
              puppies: count.puppy_count || 0,
              veterans: count.veteran_count || 0
            });
          });
          
          setAkcSpecialtyCounts(countsMap);
          
          // Variation (colour / variety) splits are applied server-side by RingSyncService.
        }).catch((error) => {
          console.error('Error fetching AKC specialty counts:', error);
          setAkcSpecialtyCounts(new Map());
        });
      } else {
        // For all-breed shows, use the existing counts API
        apiService.getAKCConformationCounts(event._id, propShow._id).then((counts) => {
        
        // Check if counts is valid and is an array
        if (!counts || !Array.isArray(counts)) {
          console.warn('AKC conformation counts response is not valid:', counts);
          return;
        }
        
        const countsMap = new Map();
        counts.forEach(count => {
          // Ensure count object has required properties
          if (!count || !count.breed_group || !count.breed) {
            console.warn('Invalid count object:', count);
            return;
          }
          
          const key = `${count.breed_group}-${count.breed}`;
          if (!countsMap.has(key)) {
            countsMap.set(key, { 
              males: 0, females: 0, puppies: 0, veterans: 0,
              group_males: 0, group_females: 0, group_puppies: 0, group_veterans: 0
            });
          }
          const existing = countsMap.get(key);
          
          // Map AKC count fields to our display format
          existing.males += count.male_count || 0;
          existing.females += count.female_count || 0;
          existing.puppies += count.puppy_count || 0;
          existing.veterans += count.veteran_count || 0;
          
          // Also store group counts for breed group logic
          existing.group_males = (existing.group_males || 0) + (count.group_male_count || 0);
          existing.group_females = (existing.group_females || 0) + (count.group_female_count || 0);
          existing.group_puppies = (existing.group_puppies || 0) + (count.group_puppy_count || 0);
          existing.group_veterans = (existing.group_veterans || 0) + (count.group_veteran_count || 0);
        });
        
        setAkcConformationCounts(countsMap);
      }).catch((error) => {
        console.error('Error fetching AKC conformation counts:', error);
        // Set empty map on error to prevent further issues
        setAkcConformationCounts(new Map());
      });
      }
    }
  }, [event._id, propShow._id, event.sanctioning_club, propShow.show_type, isSpecialtyShow]);

  // UKC Conformation: initialize breed-group, breed-specific, and special-element tiles.
  // Counts are fetched by useUKCConformationSchedule.
  // Guard is stateless: only fires when no breed-group tiles exist yet, which handles
  // reloads triggered by the score-count change effect without needing a ref-based flag.
  useEffect(() => {
    if (
      event.sanctioning_club !== Constants.sanctioning_club.UKC ||
      propShow.show_type !== Constants.show_type.conformation
    ) return;
    // Wait until the counts API call has completed. Using a loaded flag (not size)
    // so shows with zero entries aren't permanently blocked.
    if (!ukcConformationCountsLoaded) return;
    if (judges.length === 0) return;
    // `judges` can still hold the previous ring right after a ring switch or Add Ring.
    // Rebuilding from those would copy the previous ring's judges into this ring.
    if (judgesRingIdRef.current !== ringName.id) return;

    // Judges as they stand across the whole show: this ring from `judges`, other rings from
    // show state. breed_group_judges is show-level, so a configured judge who was moved to
    // another ring is still "present".
    const showWideJudges: { judge_name: string; judge_number?: string; dog_classes?: any[] }[] = [
      ...judges,
      ...(showRef.current.rings || [])
        .filter((r: any) => r.ring !== ringName.id)
        .flatMap((r: any) => r.running_order || []),
    ];

    // Only initialize when no breed group tiles are present yet — OR when configured
    // breed_group_judges are missing from every ring (e.g. show was re-saved with new
    // judge assignments but the DB rings still carry the old "fallback" layout).
    const hasBGOrBreedTiles = judges.some((j) =>
      j.dog_classes.some(
        (dc: any) =>
          dc.show_element === Constants.dog_class_element.conformation &&
          (Constants.ukc_breed_group_order as string[]).includes(dc.level)
      )
    );

    if (hasBGOrBreedTiles) {
      // Verify that every configured breed_group_judge is actually present as a judge entry.
      // If any configured judge is absent, fall through to re-initialize.
      const configuredJudgeKeys = new Set(
        (propShow.breed_group_judges || [])
          .filter((bgj: any) => bgj.judge_name)
          .map((bgj: any) => `${bgj.judge_name}|${bgj.judge_number || ''}`)
      );
      const currentJudgeKeys = new Set(
        showWideJudges.map((j) => `${j.judge_name}|${j.judge_number || ''}`)
      );
      const allConfiguredJudgesPresent =
        configuredJudgeKeys.size === 0 ||
        [...configuredJudgeKeys].every((k) => currentJudgeKeys.has(k));

      if (allConfiguredJudgesPresent) {
        // Also verify that every alpha-split breed group has the correct start_letter/end_letter
        // on its tiles.  If the DB was saved before this feature existed the tiles will be
        // missing those fields; in that case we must fall through and rebuild.
        const splitRangesPresent = (propShow.breed_group_judges || []).every((bgj: any) => {
          if (!bgj.start_letter || !bgj.end_letter) return true; // unsplit group — OK
          const judgeEntry = showWideJudges.find(
            (j) =>
              j.judge_name === bgj.judge_name &&
              (j.judge_number || '') === (bgj.judge_number || '')
          );
          return (judgeEntry?.dog_classes || []).some(
            (dc: any) =>
              dc.level === bgj.breed_group &&
              dc.start_letter === bgj.start_letter &&
              dc.end_letter === bgj.end_letter
          );
        });

        if (splitRangesPresent) {
          ukcConformationInitializedRef.current = true;
          return;
        }
        // Split ranges missing — fall through to rebuild with correct alpha ranges.
      }
      // One or more configured judges are missing — fall through to rebuild.
    }

    // Already ran once this session — don't re-fire.
    if (ukcConformationInitializedRef.current) return;

    // Determine if this ring's primary judge is a dedicated breed group judge.
    // Breed group judge rings are allowed to initialize even when other rings already have
    // classes — their tiles are ring-specific (only their assigned breed groups) and will
    // not bleed into other rings. We use showRef for fresh data to avoid stale closure.
    const currentRingId = ringName.id;
    const isBreedGroupJudgeRingLocal = (propShow.breed_group_judges || []).some(
      (bgj: any) =>
        bgj.judge_name &&
        bgj.judge_name === judges[0]?.judge_name &&
        (bgj.judge_number || '') === (judges[0]?.judge_number || '')
    );

    // Don't auto-initialize element judge rings if any other ring already has classes.
    // That means a judge is being added to an existing multi-ring setup, not during
    // initial configuration — we must not overwrite the empty dog_classes they were
    // intentionally added with.
    // Exception: dedicated breed group judge rings always initialize from their assigned groups.
    const otherRingsHaveClasses = (showRef.current.rings || []).some(
      (r: any) =>
        r.ring !== currentRingId &&
        (r.running_order || []).some((ro: any) => (ro.dog_classes || []).length > 0)
    );
    if (otherRingsHaveClasses && !isBreedGroupJudgeRingLocal) return;

    // Bootstrap: ensure all configured breed_group_judges are in the judges array
    // before calling buildUKCConformationJudges. When ring data is stale or missing,
    // the running_order may only contain "Multiple Judges" while the breed group judges
    // are configured but absent — causing buildInitialJudges to skip them (it only
    // places tiles for judges it finds in currentJudges / validJudgeKeys).
    // We skip judges that are already in a *different* ring's running_order.
    const judgesInOtherRings = new Set<string>();
    (showRef.current.rings || []).forEach((r: any) => {
      if (r.ring === ringName.id) return;
      (r.running_order || []).forEach((ro: any) => {
        judgesInOtherRings.add(`${ro.judge_name}|${ro.judge_number || ''}`);
      });
    });

    const bootstrappedJudges = [...judges];
    (propShow.breed_group_judges || []).forEach((bgj: any) => {
      if (!bgj.judge_name) return;
      const key = `${bgj.judge_name}|${bgj.judge_number || ''}`;
      if (judgesInOtherRings.has(key)) return;
      const alreadyHere = bootstrappedJudges.some(
        (j) => `${j.judge_name}|${j.judge_number || ''}` === key
      );
      if (!alreadyHere) {
        bootstrappedJudges.push({
          judge_name: bgj.judge_name,
          judge_number: bgj.judge_number || '',
          dog_classes: [],
          total_hours: 8,
        } as any);
      }
    });

    const builtJudges = buildUKCConformationJudges(bootstrappedJudges);
    // Invalidate any in-flight loadJudgeRunningOrder so it cannot overwrite the built judges.
    // loadJudgeRunningOrder starts an async chain; if it completes after this effect, its
    // setJudges(FERunningOrder) call would revert judges to DB state (1 judge) and the
    // initRef guard would then block re-initialization. Bumping the version aborts that chain.
    runningOrderLoadVersionRef.current++;
    setJudges(builtJudges);
    ukcConformationInitializedRef.current = true;
    // Persist the rebuilt schedule (including start_letter/end_letter for alpha-splits) so
    // subsequent ring-switch loads get the correct data directly from DB and skip the rebuild.
    updateRunningOrder(undefined, builtJudges, true);
  }, [
    event.sanctioning_club,
    propShow.show_type,
    propShow.breed_group_judges,
    ukcConformationCounts,
    ukcConformationCountsLoaded,
    judges,
    ringName,
    buildUKCConformationJudges,
  ]);

  // UKC Conformation: keep special element tiles (Junior Showmanship, Novice Puppy, Veteran, etc.)
  // in sync as judges state or scores change. Safe no-op for non-UKC-Conformation shows.
  useEffect(() => {
    if (
      event.sanctioning_club !== Constants.sanctioning_club.UKC ||
      propShow.show_type !== Constants.show_type.conformation ||
      judges.length === 0
    ) return;

    // Don't sync element tiles before the init build has run. The init effect and this
    // effect run in the same React commit with the same stale judges snapshot. If we
    // call setJudges here while judges only has DB data (1 judge), our update fires
    // AFTER the init's setJudges(8 judges) and overwrites it back to 1 judge.
    const hasBGOrBreedTilesForSync = judges.some((j) =>
      j.dog_classes.some(
        (dc: any) =>
          dc.show_element === Constants.dog_class_element.conformation &&
          (Constants.ukc_breed_group_order as string[]).includes(dc.level)
      )
    );
    if (!hasBGOrBreedTilesForSync) return;

    // showRef carries a BIS block hidden a moment ago before the parent re-renders with it.
    const updated = syncUKCElementTiles(judges, ringName.id, showRef.current.hidden_bis_classes || propShow.hidden_bis_classes || []);
    if (updated) setJudges(updated);
  }, [
    event.sanctioning_club,
    propShow.show_type,
    judges,
    syncUKCElementTiles,
  ]);

  // Build judges for AKC Conformation ALL-BREED shows from breed counts
  useEffect(() => {
    const isAKCConformation = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                              propShow.show_type === Constants.show_type.conformation;
    const isAllBreed = !propShow.specialty_breed || propShow.specialty_breed.trim() === '';
    
    if (isAKCConformation && isAllBreed && akcConformationCounts.size > 0 && judges.length === 0) {
      const runningTimePerDog = 2.4; // Default 2.4 minutes per dog for conformation
      
      // Create a single judge entry
      const _judges: IRunningOrderDrag[] = [{
        judge_name: "All Judges", // Placeholder, will be updated with actual judge names later
        judge_number: "",
        total_minutes: 0,
        total_hours: 0,
        dog_classes: []
      }];
      
      // Build dog_class entries for each breed group
      _judges[0].dog_classes = Array.from(akcConformationCounts.entries()).map(([breed, counts], idx) => {
        const totalEntries = counts.total || 0;
        const totalMinutes = totalEntries * runningTimePerDog;
        
        return {
          show_element: Constants.dog_class_element.conformation,
          level: breed, // Store breed as the level for all-breed shows
          entries: totalEntries,
          total_minutes: totalMinutes,
          duration: runningTimePerDog,
          type: Constants.running_order_dog_class_type.dog_class,
          index: idx
        };
      });
      
      // Calculate total minutes and hours
      _judges[0].total_minutes = _judges[0].dog_classes.reduce((sum, dc) => sum + (dc.total_minutes || 0), 0);
      _judges[0].total_hours = _judges[0].total_minutes / 60;
      
      setJudges(_judges);
    }
  }, [event.sanctioning_club, propShow.show_type, propShow.specialty_breed, akcConformationCounts, judges]);

  // Build judges for AKC Agility from class data
  useEffect(() => {
    if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
        propShow.show_type === Constants.show_type.agility &&
        akcAgilityData.loaded &&
        akcAgilityData.classes.length > 0 &&
        judges.length > 0 && 
        judges.every(j => j.dog_classes.length === 0) && // Check ALL judges
        !akcAgilityInitializedRef.current) { // Only run once
      
      akcAgilityInitializedRef.current = true; // Mark as initialized
      
      const _judges = Array.from(judges);
      
      // Create dog_class entries for each class type + level combination
      _judges[0].dog_classes = akcAgilityData.classes.map((agilityClass, idx) => {
        // Get the running time for THIS specific show element and level
        const runningTimePerRun = getRunningTimeMinutes(
          event.sanctioning_club,
          propShow.show_type,
          agilityClass.show_element, // Pass the actual show_element
          agilityClass.level         // Pass the actual level
        );
        
        const totalMinutes = agilityClass.entries * runningTimePerRun;
        
        return {
          show_element: agilityClass.show_element,
          level: agilityClass.level,
          entries: agilityClass.entries,
          total_minutes: totalMinutes,
          duration: runningTimePerRun,
          type: Constants.running_order_dog_class_type.dog_class,
          index: idx
        };
      });
      
      setJudges(_judges);
    }
  }, [event.sanctioning_club, propShow.show_type, akcAgilityData, judges]);

  // NOTE: The Dogs/Bitches (and variation) split for AKC Conformation SPECIALTY shows is
  // applied server-side by RingSyncService (on registration, armband assignment and Reset).
  // The former client-side auto-split/auto-save effect was removed: it raced the ring load
  // (counts arriving before judges meant it never ran) and duplicated the server logic.

  // Fetch all shows for trailing show timing calculations
  useEffect(() => {
    apiService.getClubEventShows(event.club_id, event._id).then((shows: IShow[]) => {
      setAllShows(shows);
    }).catch((error: any) => {
      console.error('Failed to fetch all shows:', error);
      setAllShows([]); // Fallback to empty array
    });
    
    // Also fetch all scores for the event to calculate accurate daily judge timings
    apiService.getEventScores(event._id).then((scores: any[]) => {
      setAllScores(scores);
    }).catch((error: any) => {
      console.error('Failed to fetch event scores:', error);
      setAllScores([]);
    });
  }, [event._id, event.club_id, propShow._id]); // Refresh when show changes

  // Trigger reload when score count changes (indicating new entries were added)
  useEffect(() => {
    // Skip on initial mount or when already not loaded
    if (!loaded) return;
    
    // Only trigger if the count has actually changed from what we last saw
    if (previousScoreCountRef.current === allScores.length) return;
    
    // When score count changes and we're already loaded, trigger a reload
    // This ensures the judge schedule updates automatically when new entries are added
    previousScoreCountRef.current = allScores.length;
    setLoaded(false);
  }, [allScores.length]); // Note: loaded is intentionally NOT in dependencies to prevent infinite loop

  // Refetch scores when tab becomes visible (user may have registered in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !loaded) return;
      apiService.getEventScores(event._id).then((scores: any[]) => {
        if (scores.length !== previousScoreCountRef.current) {
          previousScoreCountRef.current = scores.length;
          setAllScores(scores);
          setLoaded(false);
        }
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [event._id, loaded]);

  useEffect(() => {
    if (loaded) return; // Only run when loaded is false
    const loadVersion = ++runningOrderLoadVersionRef.current;
    
    const getRunningOrder = async (): Promise<IRunningOrder[]> => {
      try {
        // Wait for any in-flight save from a previous component instance (e.g. the
        // flush that fires when the modal closes) to complete before reading the DB.
        // Without this, a fast close-reopen races and loads stale data.
        const saveKey = `${event._id}-${propShow._id}`;
        const pendingSave = pendingShowSaves.get(saveKey);
        if (pendingSave) {
          await pendingSave;
        }

        // Fetch fresh scores first to ensure allScores is up to date
        const scores = await apiService.getEventScores(event._id);
        setAllScores(scores);

        // Build a breed-group entry count map for this show. Some conformation shows
        // store breed groups in score.breed_group rather than dog_class.level, so
        // getNumberOfEntries (which queries dog_class.level) returns 0 for those tiles.
        // We use this map as a fallback when the API returns 0 entries for a class.
        const breedGroupCounts = new Map<string, number>();
        const scoresForShow = (scores as any[]).filter((s: any) => String(s.show_id) === String(propShow._id));
        scoresForShow.forEach((s: any) => {
          const showElem = isUKCConformationShow && countsTowardUKCBreedTile(s.dog_class)
            ? Constants.dog_class_element.conformation
            : s.dog_class?.show_element;
          if (s.breed_group && showElem) {
            const key = `${showElem}|${s.breed_group}`;
            breedGroupCounts.set(key, (breedGroupCounts.get(key) || 0) + 1);
          }
        });
        
        // Always fetch fresh show structure so that judges added in a previous modal
        // session (and saved to DB) are reflected even if the show prop is stale.
        let showToUse = show;
        try {
          const freshShows = await apiService.getClubEventShows(event.club_id, event._id);
          const freshShow = freshShows?.find((s: any) => s._id === propShow._id);
          if (freshShow) {
            showToUse = freshShow;
            setShow(freshShow);
          }
        } catch {
          // Fall back to the current show prop if the refresh fails
        }

        // If we have scores for this show but no/incomplete ring structure, auto-sync to build rings from scores
        // This eliminates the need to manually click Reset for new events with registrations
        // For UKC Nosework: initial structure has show_element only (no level/section); sync creates proper level+section breakdown
        const currentRing = showToUse.rings?.find((ring: any) => ring.ring === ringName.id);
        const hasNoStructure = !currentRing?.running_order?.length;
        const showHasAnyRingStructure = !!showToUse.rings?.some(
          (ring: any) => ring.running_order && ring.running_order.length > 0
        );
        // Only auto-sync for initial setup when the entire show has no ring structure.
        // Do not auto-sync when users intentionally add an empty ring.
        const shouldAutoSyncForMissingStructure = hasNoStructure && !showHasAnyRingStructure;
        const isUKCNosework = event.sanctioning_club === Constants.sanctioning_club.UKC &&
          propShow.show_type === Constants.show_type.nosework;
        const hasIncompleteUKCNoseworkStructure = isUKCNosework && currentRing?.running_order?.some(
          (ro: any) => ro.dog_classes?.some((dc: any) =>
            dc.type === Constants.running_order_dog_class_type.dog_class && dc.show_element && !dc.level
          )
        );
        const scoresForThisShow = scores.filter((s: any) => s.show_id === propShow._id);
        // Also auto-sync when the ring has judge entries but all dog_classes are empty
        // (e.g. show created before judge-schedule support was added for this club/type)
        const hasJudgesButNoClasses = !hasNoStructure && currentRing?.running_order?.every(
          (ro: any) => !ro.dog_classes || ro.dog_classes.filter(
            (dc: any) => dc.type === Constants.running_order_dog_class_type.dog_class
          ).length === 0
        );
        if ((scoresForThisShow.length > 0 && (shouldAutoSyncForMissingStructure || hasIncompleteUKCNoseworkStructure)) ||
            hasJudgesButNoClasses) {
          const syncedShow = await apiService.syncShowRingsWithScores(event._id, propShow._id);
          setShow(syncedShow);
          onUpdate(syncedShow);
          showToUse = syncedShow;
        }
        
        const ringForStructure = showToUse.rings?.find((ring: any) => ring.ring === ringName.id);
        if (!ringForStructure?.running_order?.length) {
          return [];
        }
        
        const response = await apiService.getShowRunningOrderSummary(event._id, propShow._id, ringName.id);
        
        return ringForStructure.running_order.map((ro: any) => {
            let foundRo:any = response.find((_ro: any) => { return _ro.judge_name === ro.judge_name && _ro.judge_number === ro.judge_number });

            let newDogClasses:any = [];

            // Process all items from saved data to preserve breaks and order
            for (let rdc in ro.dog_classes) {
              if (isUKCConformationShow && isUKCConformationUnscheduledLevel(ro.dog_classes[rdc].level)) {
                continue;
              }
              if (ro.dog_classes[rdc].type === "dog_class") {
                // Special handling for UKC Conformation - breed groups won't be in API response
                const isUKCConformation = event.sanctioning_club === Constants.sanctioning_club.UKC && 
                                         propShow.show_type === Constants.show_type.conformation;
                
                // Special handling for AKC Agility - similar to UKC Conformation
                const isAKCAgility = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                                    propShow.show_type === Constants.show_type.agility;
                
                // Special handling for AKC Conformation Specialty with sex split
                const isAKCConformationSpecialty = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                                                   propShow.show_type === Constants.show_type.conformation &&
                                                   propShow.specialty_breed && propShow.specialty_breed.trim() !== '';
                
                // Special handling for UKC Nosework with sections
                const isUKCNosework = event.sanctioning_club === Constants.sanctioning_club.UKC && 
                                     propShow.show_type === Constants.show_type.nosework;
                
                if (
                  isUKCConformation &&
                  [
                    Constants.dog_class_element.conformation,
                    Constants.dog_class_element.conformation_altered,
                    Constants.dog_class_element.junior_showmanship,
                    Constants.dog_class_element.conformation_non_licensed,
                  ].includes(ro.dog_classes[rdc].show_element as string)
                ) {
                  // For UKC Conformation, preserve the breed group from saved data.
                  // Use breedGroupCounts as the authoritative entry count so that
                  // the "Hide empty elements and levels" filter works correctly.
                  const bgKey = `${ro.dog_classes[rdc].show_element}|${ro.dog_classes[rdc].level}`;
                  const bgEntries = breedGroupCounts.get(bgKey) ?? (ro.dog_classes[rdc].entries || 0);
                  newDogClasses.push({
                    ...ro.dog_classes[rdc],
                    entries: bgEntries,
                  });
                } else if (isAKCConformationSpecialty && ro.dog_classes[rdc].sex_split) {
                  // For AKC Conformation Specialty with sex split, preserve saved data
                  // The entries and times are already calculated with sex-specific counts
                  newDogClasses.push({
                    ...ro.dog_classes[rdc]
                  });
                } else if (isUKCNosework) {
                  // For UKC Nosework, sections are merged — match by element+level only
                  let foundApiData = foundRo?.dog_classes?.find((apiClass: any) =>
                    apiClass.show_element === ro.dog_classes[rdc].show_element &&
                    apiClass.level === ro.dog_classes[rdc].level
                  );
                  if (foundApiData) {
                    const constantDuration = getRunningTimeMinutes(
                      event.sanctioning_club,
                      propShow.show_type,
                      foundApiData.show_element,
                      foundApiData.level
                    );
                    // For split tiles the API summary covers all splits combined, so its
                    // entries/total_minutes would be inflated. Use the DB-saved values instead.
                    const noseworkIsSplit = ro.dog_classes[rdc].split_number !== undefined;
                    const noseworkEntries = noseworkIsSplit
                      ? (ro.dog_classes[rdc].dog_run_order?.length ?? ro.dog_classes[rdc].entries ?? 0)
                      : foundApiData.entries;
                    const noseworkMinutes = noseworkIsSplit
                      ? ro.dog_classes[rdc].total_minutes
                      : foundApiData.total_minutes;
                    newDogClasses.push({
                      ...ro.dog_classes[rdc],
                      entries: noseworkEntries,
                      total_minutes: noseworkMinutes,
                      duration: foundApiData.duration || ro.dog_classes[rdc].duration || constantDuration
                    });
                  } else {
                    newDogClasses.push({ ...ro.dog_classes[rdc] });
                  }
                } else if (isAKCAgility) {
                  // For AKC Agility, look up current entries from akcAgilityData
                  const classKey = `${ro.dog_classes[rdc].show_element}-${ro.dog_classes[rdc].level}`;
                  const currentClass = akcAgilityData.classes.find(
                    c => `${c.show_element}-${c.level}` === classKey
                  );
                  
                  if (currentClass) {
                    // Update with current entry counts from akcAgilityData
                    // Get the running time for THIS specific show element and level
                    const runningTimePerRun = getRunningTimeMinutes(
                      event.sanctioning_club,
                      propShow.show_type,
                      currentClass.show_element, // Pass the actual show_element
                      currentClass.level         // Pass the actual level
                    );
                    
                    let totalEntries = currentClass.entries;
                    let totalMinutes = currentClass.entries * runningTimePerRun;
                    
                    // If this class is a parent (not grouped with anything), check if it has children
                    if (!ro.dog_classes[rdc].group_with) {
                      const parentIdentifier = `${ro.dog_classes[rdc].show_element}:${ro.dog_classes[rdc].level}`;
                      const parentLevel = ro.dog_classes[rdc].level;
                      
                      // Find all child classes in the running order that are grouped with this parent
                      // Check BOTH formats: NEW (parent identifier with ":") and OLD (simple parent level name)
                      const childClasses = ro.dog_classes.filter((dc: any) => 
                        (dc.group_with === parentIdentifier || dc.group_with === parentLevel) && 
                        dc.show_element === ro.dog_classes[rdc].show_element
                      );
                      
                      // Add up entries from all children
                      childClasses.forEach((child: any) => {
                        const childKey = `${child.show_element}-${child.level}`;
                        const childData = akcAgilityData.classes.find(
                          c => `${c.show_element}-${c.level}` === childKey
                        );
                        if (childData) {
                          totalEntries += childData.entries;
                          totalMinutes += childData.entries * runningTimePerRun;
                        }
                      });
                    }
                    
                    newDogClasses.push({
                      ...ro.dog_classes[rdc],
                      entries: totalEntries,
                      total_minutes: totalMinutes,
                      duration: runningTimePerRun
                    });
                    
                  } else {
                    // If not found in akcAgilityData, preserve the saved data
                    // (e.g., when RunOrderTab just updated but akcAgilityData hasn't refreshed)
                    newDogClasses.push({
                      ...ro.dog_classes[rdc]
                    });
                  }
                } else {
                  // Standard logic for other show types
                // Find matching API data for this dog class
                let foundApiData = foundRo?.dog_classes?.find((apiClass: any) => 
                  apiClass.show_element === ro.dog_classes[rdc].show_element && 
                  (apiClass.level || '') === (ro.dog_classes[rdc].level || '') &&
                  (apiClass.section || '') === (ro.dog_classes[rdc].section || '')
                );

                if (foundApiData) {
                    // Check if this class is enabled in show_elements
                    // For some show types, show_elements are stored by show_element only (no level)
                    // For C-WAGS Obedience and Rally (NOT Scent), check by level and section
                    const isConformation = propShow.show_type === Constants.show_type.conformation;
                    const isShowElementOnlyType = [
                      Constants.show_type.conformation,
                      Constants.show_type.farm_dog,
                      Constants.show_type.att,
                      Constants.show_type.dock_diving,
                    ].includes(propShow.show_type);
                    const isCWagsWithSections = event.sanctioning_club === Constants.sanctioning_club.C_WAGS && 
                      (propShow.show_type === Constants.show_type.obedience || 
                       propShow.show_type === Constants.show_type.rally_obedience);
                    // 4-H Agility uses Division A/B sections (see Constants.dog_class_section.fh_agility_division_a/b) —
                    // multiple elements each with their own levels+sections, so match by element+level+section.
                    const isFHAgilityWithSections = event.sanctioning_club === Constants.sanctioning_club.FH &&
                      propShow.show_type === Constants.show_type.agility;
                    
                    let isClassEnabled = false;
                    if (propShow.show_type === Constants.show_type.scentwork) {
                      isClassEnabled = isOfferedShowClass(propShow.show_elements, foundApiData);
                    } else if (isShowElementOnlyType || isConformation) {
                      isClassEnabled = true;
                    } else if (isCWagsWithSections) {
                      // For C-WAGS Obedience/Rally with sections, check by level and section
                      isClassEnabled = propShow.show_elements?.some((se: any) => 
                        se.level === foundApiData.level && 
                        se.section === foundApiData.section
                      ) ?? false;
                    } else if (isFHAgilityWithSections) {
                      // For 4-H Agility, check by element, level, and section (Division A/B)
                      isClassEnabled = propShow.show_elements?.some((se: any) =>
                        se.show_element === foundApiData.show_element &&
                        se.level === foundApiData.level &&
                        se.section === foundApiData.section
                      ) ?? false;
                    } else if (event.sanctioning_club === Constants.sanctioning_club.ABI &&
                               propShow.show_type === Constants.show_type.scent_dog_games) {
                      // For ABI SDG: show_element="Scent Dog Games", level=game name, section="Level 1/2"
                      isClassEnabled = propShow.show_elements?.some((se: any) =>
                        se.show_element === foundApiData.show_element &&
                        se.level === foundApiData.level &&
                        (se.section || '') === (foundApiData.section || '')
                      ) ?? false;
                    } else if (propShow.show_type === Constants.show_type.obedience_show_n_go ||
                               propShow.show_type === Constants.show_type.general_rally) {
                      // For Obedience Show N Go and General Rally, show_elements uses levels-as-keys pattern:
                      // { show_element: "Novice" } with no level field - match by level
                      isClassEnabled = propShow.show_elements?.some((se: any) =>
                        se.show_element === foundApiData.level
                      ) ?? false;
                    } else {
                      // Standard check by element + level
                      isClassEnabled = propShow.show_elements?.some((se: any) => 
                        se.show_element === foundApiData.show_element && se.level === foundApiData.level
                      ) ?? false;
                    }
                    
                    // Only add the class if it's enabled (we'll filter by entries during rendering)
                    if (isClassEnabled) {
                  // Calculate duration from constants as fallback
                  const constantDuration = getRunningTimeMinutes(
                    event.sanctioning_club,
                    propShow.show_type,
                    foundApiData.show_element,
                    foundApiData.level
                  );
                  
                  // For split tiles, the API summary returns counts for the full class (not the split).
                  // Use the DB-saved entries/total_minutes which were set correctly by splitDogClass.
                  const isSplitTile = ro.dog_classes[rdc].split_number !== undefined;

                  // For conformation shows that organise by breed group, scores store the
                  // breed group in score.breed_group rather than dog_class.level, so the
                  // backend's getNumberOfEntries (which filters on dog_class.level) returns 0.
                  // Fall back to the breed-group count map built from raw scores above.
                  const bgKey = `${foundApiData.show_element}|${ro.dog_classes[rdc].level}`;
                  const breedGroupFallback = foundApiData.entries === 0
                    ? (breedGroupCounts.get(bgKey) ?? 0)
                    : 0;

                  const splitEntries = isSplitTile
                    ? (ro.dog_classes[rdc].dog_run_order?.length ?? ro.dog_classes[rdc].entries ?? 0)
                    : (foundApiData.entries || breedGroupFallback);
                  const splitMinutes = isSplitTile
                    ? ro.dog_classes[rdc].total_minutes
                    : (foundApiData.total_minutes || breedGroupFallback * (foundApiData.duration || ro.dog_classes[rdc].duration || 4));

                  let ndc: any = {
                    entries: splitEntries,
                    level: foundApiData.level,
                    show_element: foundApiData.show_element,
                    type: ro.dog_classes[rdc].type ? ro.dog_classes[rdc].type : foundApiData.type,
                    total_minutes: splitMinutes,
                    duration: foundApiData.duration || ro.dog_classes[rdc].duration || constantDuration || (foundApiData.entries > 0 ? Math.round(foundApiData.total_minutes / foundApiData.entries) : 2),
                    time_limit: foundApiData.time_limit,
                    num_hides: foundApiData.num_hides,
                    dog_run_order: ro.dog_classes[rdc].dog_run_order, // Preserve custom dog order
                  };
                  // Preserve split fields for split tiles
                  if (isSplitTile) {
                    ndc.split_number = ro.dog_classes[rdc].split_number;
                    ndc.split_total = ro.dog_classes[rdc].split_total;
                  }
                  
                  // Preserve section for shows that use it (C-WAGS Obedience/Rally, UKC Nosework, etc.)
                  if (foundApiData.section || ro.dog_classes[rdc].section) {
                    ndc.section = foundApiData.section || ro.dog_classes[rdc].section;
                  }
                  
                  if (ro.dog_classes[rdc].group_with !== undefined) {
                    ndc.group_with = ro.dog_classes[rdc].group_with;
                  }

                  if (ro.dog_classes[rdc].label !== undefined) {
                    ndc.label = ro.dog_classes[rdc].label;
                  }

                  // Preserve sex_split for AKC Conformation specialty shows
                  if (ro.dog_classes[rdc].sex_split !== undefined) {
                    ndc.sex_split = ro.dog_classes[rdc].sex_split;
                  }

                  newDogClasses.push(ndc);
                    }
                  } else if (
                    propShow.show_type === Constants.show_type.scentwork &&
                    !isOfferedShowClass(propShow.show_elements, ro.dog_classes[rdc])
                  ) {
                    // Class is not offered on this show.
                  } else {
                    // API didn't return data for this class (scores may reference a different judge_name
                    // than what's in the running_order, e.g. after a manual judge reassignment).
                    // Use the saved entries/run_order count as fallback so the class stays visible.
                    const constantDuration = getRunningTimeMinutes(
                      event.sanctioning_club,
                      propShow.show_type,
                      ro.dog_classes[rdc].show_element,
                      ro.dog_classes[rdc].level
                    );
                    const savedEntries = ro.dog_classes[rdc].entries || (ro.dog_classes[rdc].dog_run_order || []).length || 0;
                    
                    let ndc: any = {
                      ...ro.dog_classes[rdc],
                      entries: savedEntries,
                      duration: ro.dog_classes[rdc].duration || constantDuration || 2,
                      total_minutes: savedEntries * (ro.dog_classes[rdc].duration || constantDuration || 2),
                    };
                    
                    newDogClasses.push(ndc);
                  }
                }
              } else if (ro.dog_classes[rdc].type === Constants.running_order_dog_class_type.break) {
                // This is a break - use saved data directly since API doesn't include breaks
                let breakItem = { ...ro.dog_classes[rdc] };
                
                // Ensure it has a name property for backwards compatibility
                if (!breakItem.name) {
                  breakItem.name = "Break";
                }
                
                // Ensure break has total_minutes calculated from duration for timing calculations
                if (breakItem.duration && !breakItem.total_minutes) {
                  breakItem.total_minutes = breakItem.duration;
                }
                
                newDogClasses.push(breakItem);
              }
            }

            let newRo:any = { ...{ judge_name: ro.judge_name, judge_number: ro.judge_number }, ...{ dog_classes: newDogClasses } };

            if (foundRo) {
              newRo.total_minutes = foundRo.total_minutes;
            }

            return newRo;
          });
      } catch {
        return [];
      }
    }

    const generateRunningOrder = (_ro: IRunningOrder[]) => {
      let index:number = 0;
      let _fe_running_order:IRunningOrderDrag[] = [];
      let grouped_dog_classes:IRunningOrderDogClassDrag[] = [];

      for (let r in _ro) {
        // Clone dog_classes so grouped class merging for UI does not mutate the source data
        const clonedDogClasses = (_ro[r].dog_classes || []).map((dc: any) => ({ ...dc }));
        let _running_order_item:any = {
          ..._ro[r],
          total_hours: 8,
          dog_classes: clonedDogClasses
        };

        for (let d in _running_order_item.dog_classes) {
          let dc:IRunningOrderDogClassDrag = _running_order_item.dog_classes[d];

          // Check both legacy group_with property and new dynamic grouping
          const levelKey = `${dc.show_element}-${dc.level}`;
          if (dc.group_with !== undefined || levelGroups.has(levelKey)) {
            grouped_dog_classes.push(dc);
            
            // For dynamic grouping, set the group_with property to maintain compatibility
            if (!dc.group_with && levelGroups.has(levelKey)) {
              dc.group_with = levelGroups.get(levelKey)?.split('-')[1]; // Extract level from "show_element-level"
            }
          }

          _running_order_item.dog_classes[d].index = index;
          index++;
        }

        _fe_running_order.push(_running_order_item);
      }

      /* ADD GROUPED DOG CLASS TO MAIN DOG CLASS */
      // Sort grouped_dog_classes by group_order to maintain selection order in labels
      const sortedGroupedClasses = [...grouped_dog_classes].sort((a, b) => {
        const orderA = a.group_order ?? 999;
        const orderB = b.group_order ?? 999;
        return orderA - orderB;
      });
      
      for (let fe in _fe_running_order) {
        for (let _gdc in sortedGroupedClasses) {
          // Match parent by BOTH formats:
          // NEW format: "show_element:level" (e.g., "Obedience:Preferred Open")
          // OLD format: simple level name (e.g., "Open B")
          const parentIdentifier = sortedGroupedClasses[_gdc].group_with;
          
          let hasGroupLevel:IRunningOrderDogClassDrag[] = _fe_running_order[fe].dog_classes.filter((dc: any) => { 
            const dcIdentifier = `${dc.show_element}:${dc.level}`;
            // Match NEW format (with ":") OR OLD format (simple level name match + same show_element)
            return parentIdentifier === dcIdentifier || 
                   (parentIdentifier === dc.level && dc.show_element === sortedGroupedClasses[_gdc].show_element);
          });

          if (hasGroupLevel.length > 0) {
            // For AKC Agility, entries are already summed in getRunningOrder
            // Just build the label to show grouped levels
            const isAKCAgility = event.sanctioning_club === Constants.sanctioning_club.AKC && 
                                propShow.show_type === Constants.show_type.agility;
            
            if (!isAKCAgility) {
              // For other show types, add entries from grouped level
              hasGroupLevel[0].entries = (hasGroupLevel[0].entries || 0) + (sortedGroupedClasses[_gdc].entries || 0);

              // Add total_minutes from grouped level for correct timing calculations
              if (sortedGroupedClasses[_gdc].total_minutes) {
                hasGroupLevel[0].total_minutes = (hasGroupLevel[0].total_minutes || 0) + sortedGroupedClasses[_gdc].total_minutes;
              }
            }

            // Build label showing all grouped levels
            const existingLabel = hasGroupLevel[0].label || "";
            const newLevelLabel = "(" + sortedGroupedClasses[_gdc].level + ")";
            hasGroupLevel[0].label = existingLabel ? existingLabel + " " + newLevelLabel : newLevelLabel;
          }
        }
      }

      return _fe_running_order;
    }

    const loadJudgeRunningOrder = async() => {
      let runningOrder:IRunningOrder[] = await getRunningOrder();
      if (loadVersion !== runningOrderLoadVersionRef.current) return;
      let FERunningOrder:IRunningOrderDrag[] = generateRunningOrder(runningOrder);
      if (loadVersion !== runningOrderLoadVersionRef.current) return;

      // Update the show state with enriched running order data for accurate timing calculations
      setShow((prevShow) => {
        if (!prevShow.rings) return prevShow;
        const updatedRings = prevShow.rings.map((r: IShowRing) => {
          if (r.ring !== ringName.id) return r;
          return { ...r, running_order: runningOrder };
        });
        return { ...prevShow, rings: updatedRings };
      });

      if (loadVersion !== runningOrderLoadVersionRef.current) return;
      judgesRingIdRef.current = ringName.id;
      setJudges(FERunningOrder);
    }

    loadJudgeRunningOrder();

    if (loadVersion === runningOrderLoadVersionRef.current) {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, propShow._id, propShow.show_elements, ringName.id, show.rings, levelGroups, akcAgilityData.loaded, loaded]);

  const getIndexOffset = useCallback((_judges: IRunningOrderDrag[], judgeIndex: number) => {
    let offset:number = 0;

    if (judgeIndex > 0) {
      for (let j in judges) {
        if (Number(j) < judgeIndex) {
          offset += judges[j].dog_classes.length;
        }
      }
    }

    return offset;
  }, [judges]);

  const reorder = useCallback((_judges: IRunningOrderDrag[], startIndex: number, endIndex: number, judgeIndex: number) => {
    const offset:number = getIndexOffset(_judges, judgeIndex);

    const [removed] = _judges[judgeIndex].dog_classes.splice(startIndex - offset, 1);
    _judges[judgeIndex].dog_classes.splice(endIndex - offset, 0, removed);

    return _judges;
  }, [getIndexOffset]);

  const move = useCallback((_judges: IRunningOrderDrag[], source: any, destination: any) => {
    const offsetSource:number = getIndexOffset(_judges, Number(source.droppableId));
    const offsetDest:number = getIndexOffset(_judges, Number(destination.droppableId));

    const [removed] = _judges[source.droppableId].dog_classes.splice((source.index - offsetSource), 1);
    _judges[destination.droppableId].dog_classes.splice((destination.index - offsetDest), 0, removed);

    return _judges;
  }, [getIndexOffset]);

  const getJudgeEntryCount = useCallback((judge: IRunningOrderDrag) => {
    // Always use the local judge.dog_classes to count entries
    // This ensures drag-and-drop changes are immediately reflected
    // Only count actual classes (those with a level), not placeholder/grouping classes
    return (judge.dog_classes || []).reduce((sum, dc) => {
      if (dc.type === Constants.running_order_dog_class_type.break) return sum;
      
      // Skip classes that are grouped into other classes to avoid double-counting
      if (dc.group_with) return sum;
      
      // IMPORTANT: Only count classes that have a level (actual classes)
      // Placeholder classes (for grouping) have show_element but no level.
      // Junior Showmanship is the exception: it's one real class with no level.
      if (!dc.level && dc.show_element !== Constants.dog_class_element.junior_showmanship) return sum;
      
      return sum + (dc.entries || 0);
    }, 0);
  }, []);

  const getElementCountsDisplay = useCallback((element: IRunningOrderDogClassDrag, judgeName?: string) => {
    // For AKC Conformation, show breed-specific counts in format: male-female-puppy-veteran
    if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
        propShow.show_type === Constants.show_type.conformation) {
      
      // For specialty shows, use judge+level based counts
      if (isSpecialtyShow && judgeName) {
        // If this class has sex_split, just show the entry count for that sex
        if (element.sex_split) {
          const entries = element.entries || 0;
          return `${entries} ${entries === 1 ? 'Entry' : 'Entries'}`;
        }

        if (!akcSpecialtyCounts || akcSpecialtyCounts.size === 0) {
          return `${element.entries || 0} ${((element.entries || 0) === 1) ? 'Entry' : 'Entries'}`;
        }

        const level = element.level || '';
        
        // For Junior Showmanship, sum across ALL judges (not tied to specific judge in schedule)
        if (element.show_element === Constants.dog_class_element.junior_showmanship) {
          let totalMales = 0, totalFemales = 0;

          akcSpecialtyCounts.forEach((counts, key) => {
            // Key format: "judgeName-classLevel"
            // Since classLevel can contain dashes, split only on first dash
            const firstDashIndex = key.indexOf('-');
            if (firstDashIndex === -1) return;
            
            const keyJudge = key.substring(0, firstDashIndex);
            const keyLevel = key.substring(firstDashIndex + 1);
            
            if (keyLevel === level) {
              totalMales += counts.males || 0;
              totalFemales += counts.females || 0;
            }
          });
          
          const total = totalMales + totalFemales;
          return `${total} ${total === 1 ? 'Entry' : 'Entries'}`;
        }
        
        // For regular conformation classes, use the specific judge
        const key = `${judgeName}-${level}`;
        const counts = akcSpecialtyCounts.get(key);

        if (counts) {
          const totalMales = counts.males || 0;
          const totalFemales = counts.females || 0;

          // Format: total entries (for non-split classes like Best of Breed)
          const total = totalMales + totalFemales;
          return `${total} ${total === 1 ? 'Entry' : 'Entries'}`;
        }

        // No counts found, return 0s
        return `0 Entries`;
      }
      
      // For all-breed shows, use the existing logic
      // If counts haven't loaded yet or there's an error, fall back to regular entry count
      if (!akcConformationCounts || akcConformationCounts.size === 0) {
        return `${element.entries || 0} ${((element.entries || 0) === 1) ? 'Entry' : 'Entries'}`;
      }
      
      const level = element.level || '';
      let totalMales = 0, totalFemales = 0, totalPuppies = 0, totalVeterans = 0;
      
      try {
        // Special handling for Junior Showmanship
        if (element.show_element === Constants.dog_class_element.junior_showmanship) {
          // For Junior Showmanship, match by the actual class level
          // The key format is "judgeName-level", so we need to find all keys that end with this element's level
          const elementLevel = element.level || '';
          akcConformationCounts.forEach((counts, key) => {
            // Check if this key matches the element's level (format: "judgeName-level")
            const [keyJudge, keyLevel] = key.split('-');
            if (keyLevel === elementLevel) {
              totalMales += counts.males || 0;
              totalFemales += counts.females || 0;
              totalPuppies += counts.puppies || 0;
              totalVeterans += counts.veterans || 0;
            }
          });
        } else {
          // Look for matching counts in the map
          akcConformationCounts.forEach((counts, key) => {
            const [keyBreedGroup, keyBreed] = key.split('-');
            
            // Strategy 1: Exact match with breed group and breed in level (breed-specific)
            if (level.includes(keyBreedGroup) && level.includes(keyBreed)) {
              // Use full breed counts for breed-specific entries
              totalMales += counts.males || 0;
              totalFemales += counts.females || 0;
              totalPuppies += counts.puppies || 0;
              totalVeterans += counts.veterans || 0;
            }
            // Strategy 2: Match just the breed group (for breed group judges)
            else if (level === keyBreedGroup) {
              // Use group counts (excluding entries with breed-specific judges)
              totalMales += counts.group_males || 0;
              totalFemales += counts.group_females || 0;
              totalPuppies += counts.group_puppies || 0;
              totalVeterans += counts.group_veterans || 0;
            }
          });
        }
        
        return `${totalMales}-${totalFemales}-${totalPuppies}-${totalVeterans}`;
      } catch (error) {
        console.error('Error calculating element AKC conformation counts display:', error);
        return `${element.entries || 0} ${((element.entries || 0) === 1) ? 'Entry' : 'Entries'}`;
      }
    }
    
    // For UKC Conformation, show breed-specific counts in the format specified by the user
    if (event.sanctioning_club === Constants.sanctioning_club.UKC && 
        propShow.show_type === Constants.show_type.conformation) {
      // If counts haven't loaded yet or there's an error, fall back to regular entry count
      if (!ukcConformationCounts || ukcConformationCounts.size === 0) {
    return `${element.entries || 0} ${((element.entries || 0) === 1) ? 'Entry' : 'Entries'}`;
      }
      
      const level = element.level || '';
      const counts = { 
        male_count: 0, female_count: 0, champion_count: 0, grand_champion_count: 0,
        novice_puppy_count: 0, brace_count: 0, veteran_count: 0,
        alt_male_count: 0, alt_female_count: 0, alt_champion_count: 0, alt_grand_champion_count: 0,
        alt_novice_puppy_count: 0, alt_brace_count: 0, alt_veteran_count: 0
      };
      
      try {
        // Look for matching counts in the map
        ukcConformationCounts.forEach((breedCounts, key) => {
          const [keyBreedGroup, keyBreed] = key.split('-');
          
          // Match the level (breed name) with the key
          if (level.includes(keyBreed) || level === keyBreedGroup) {
            counts.male_count += breedCounts.male_count || 0;
            counts.female_count += breedCounts.female_count || 0;
            counts.champion_count += breedCounts.champion_count || 0;
            counts.grand_champion_count += breedCounts.grand_champion_count || 0;
            counts.novice_puppy_count += breedCounts.novice_puppy_count || 0;
            counts.brace_count += breedCounts.brace_count || 0;
            counts.veteran_count += breedCounts.veteran_count || 0;
            counts.alt_male_count += breedCounts.alt_male_count || 0;
            counts.alt_female_count += breedCounts.alt_female_count || 0;
            counts.alt_champion_count += breedCounts.alt_champion_count || 0;
            counts.alt_grand_champion_count += breedCounts.alt_grand_champion_count || 0;
            counts.alt_novice_puppy_count += breedCounts.alt_novice_puppy_count || 0;
            counts.alt_brace_count += breedCounts.alt_brace_count || 0;
            counts.alt_veteran_count += breedCounts.alt_veteran_count || 0;
          }
        });
        
        // Format: Breed Name (total entries)
        // Regular: M-F-CH-GRCH NP-BR VET-count
        // Altered: M-F-CH-GRCH NP-BR VET-count
        const totalEntries = counts.male_count + counts.female_count + counts.champion_count + 
                            counts.grand_champion_count + counts.novice_puppy_count + counts.brace_count + 
                            counts.veteran_count + counts.alt_male_count + counts.alt_female_count + 
                            counts.alt_champion_count + counts.alt_grand_champion_count + 
                            counts.alt_novice_puppy_count + counts.alt_brace_count + counts.alt_veteran_count;
        
        let result = `${level} (${totalEntries} ${totalEntries === 1 ? 'entry' : 'entries'})\n`;
        result += `Regular: ${counts.male_count}-${counts.female_count}-${counts.champion_count}-${counts.grand_champion_count} NP-${counts.novice_puppy_count} BR-${counts.brace_count} VET-${counts.veteran_count}\n`;
        result += `Altered: ${counts.alt_male_count}-${counts.alt_female_count}-${counts.alt_champion_count}-${counts.alt_grand_champion_count} NP-${counts.alt_novice_puppy_count} BR-${counts.alt_brace_count} VET-${counts.alt_veteran_count}`;
        
        return result;
      } catch (error) {
        console.error('Error calculating element UKC conformation counts display:', error);
        return `${element.entries || 0} ${((element.entries || 0) === 1) ? 'Entry' : 'Entries'}`;
      }
    }
    
    // For non-Conformation, show regular entry count
    return `${element.entries || 0} ${((element.entries || 0) === 1) ? 'Entry' : 'Entries'}`;
  }, [event.sanctioning_club, propShow.show_type, akcConformationCounts, akcSpecialtyCounts, ukcConformationCounts, isSpecialtyShow]);

  const getJudgeCountsDisplay = useCallback((judge: IRunningOrderDrag) => {
    // For AKC Conformation, show breed-specific counts in format: male-female-puppy-veteran
    if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
        propShow.show_type === Constants.show_type.conformation) {
      
      // For specialty shows, sum up counts for all classes under this judge
      if (isSpecialtyShow) {
        // If classes have sex_split, just sum the entries directly
        const hasSexSplit = (judge.dog_classes || []).some(dc => dc.sex_split);
        
        if (hasSexSplit) {
          // Sum entries directly from split classes
          let totalDogs = 0, totalBitches = 0;
          (judge.dog_classes || []).forEach((dc) => {
            if (dc.type === Constants.running_order_dog_class_type.break || dc.group_with) return;
            
            if (dc.sex_split === "Dogs") {
              totalDogs += dc.entries || 0;
            } else if (dc.sex_split === "Bitches") {
              totalBitches += dc.entries || 0;
            } else {
              // Non-split classes (Best of Breed, etc.) - add to both? or just total
              totalDogs += Math.floor((dc.entries || 0) / 2);
              totalBitches += Math.ceil((dc.entries || 0) / 2);
            }
          });
          
          const total = totalDogs + totalBitches;
          return `${total} - ${totalDogs} - ${totalBitches}`;
        }
        
        if (!akcSpecialtyCounts || akcSpecialtyCounts.size === 0) {
          return `${getJudgeEntryCount(judge)} Entries`;
        }
        
        let totalMales = 0, totalFemales = 0;
        const judgeName = judge.judge_name || '';
        const countedLevels = new Set<string>();
        
        try {
          (judge.dog_classes || []).forEach((dc) => {
            if (dc.type === Constants.running_order_dog_class_type.break || dc.group_with) return;
            
            const level = dc.level || '';
            // Avoid double-counting the same level
            if (countedLevels.has(level)) return;
            countedLevels.add(level);
            
            // For Junior Showmanship, sum across ALL judges (not tied to specific judge)
            if (dc.show_element === Constants.dog_class_element.junior_showmanship) {
              akcSpecialtyCounts.forEach((counts, key) => {
                // Key format: "judgeName-classLevel"
                // Since classLevel can contain dashes, split only on first dash
                const firstDashIndex = key.indexOf('-');
                if (firstDashIndex === -1) return;
                
                const keyJudge = key.substring(0, firstDashIndex);
                const keyLevel = key.substring(firstDashIndex + 1);
                
                if (keyLevel === level) {
                  totalMales += counts.males || 0;
                  totalFemales += counts.females || 0;
                }
              });
            } else {
              // For regular classes, use judge-specific counts
              const key = `${judgeName}-${level}`;
              const counts = akcSpecialtyCounts.get(key);
              
              if (counts) {
                totalMales += counts.males || 0;
                totalFemales += counts.females || 0;
              }
            }
          });
          
          const total = totalMales + totalFemales;
          return `${total} - ${totalMales} - ${totalFemales}`;
        } catch (error) {
          console.error('Error calculating specialty judge counts:', error);
          return `${getJudgeEntryCount(judge)} Entries`;
        }
      }
      
      // For all-breed shows, use existing logic
      // If counts haven't loaded yet or there's an error, fall back to regular entry count
      if (!akcConformationCounts || akcConformationCounts.size === 0) {
        return `${getJudgeEntryCount(judge)} Entries`;
      }
      
      let totalMales = 0, totalFemales = 0, totalPuppies = 0, totalVeterans = 0;
      
      try {
        // Track which breeds we've already counted to avoid double-counting
        const countedBreeds = new Set();
        
        (judge.dog_classes || []).forEach((dc) => {
          if (dc.type === Constants.running_order_dog_class_type.break || dc.group_with) return;
          
          // Special handling for Junior Showmanship
          if (dc.show_element === Constants.dog_class_element.junior_showmanship) {
            // For Junior Showmanship, match by the actual class level
            // The key format is "judgeName-level", so we need to find the exact match
            const dcLevel = dc.level || '';
            const key = `${judge.judge_name}-${dcLevel}`;
            if (akcConformationCounts.has(key) && !countedBreeds.has(key)) {
              const counts = akcConformationCounts.get(key)!;
              totalMales += counts.males || 0;
              totalFemales += counts.females || 0;
              totalPuppies += counts.puppies || 0;
              totalVeterans += counts.veterans || 0;
              countedBreeds.add(key);
            }
          } else {
            // The level contains the breed group and breed info
            const level = dc.level || '';
            
            // Look for matching counts in the map
            // The API returns keys in format "breed_group-breed"
            // We need to find the matching entry
            
            akcConformationCounts.forEach((counts, key) => {
              // Check if we've already counted this breed for this judge
              if (countedBreeds.has(key)) {
                return;
              }
              
              // Try different matching strategies
              const [keyBreedGroup, keyBreed] = key.split('-');
              
              // Strategy 1: Exact match with breed group and breed in level (breed-specific)
              if (level.includes(keyBreedGroup) && level.includes(keyBreed)) {
                // Use full breed counts for breed-specific entries
                totalMales += counts.males || 0;
                totalFemales += counts.females || 0;
                totalPuppies += counts.puppies || 0;
                totalVeterans += counts.veterans || 0;
                countedBreeds.add(key);
              }
              // Strategy 2: Match just the breed group (for breed group judges)
              else if (level === keyBreedGroup) {
                // Use group counts (excluding entries with breed-specific judges)
                totalMales += counts.group_males || 0;
                totalFemales += counts.group_females || 0;
                totalPuppies += counts.group_puppies || 0;
                totalVeterans += counts.group_veterans || 0;
                countedBreeds.add(key);
              }
            });
          }
        });
        
        return `${totalMales}-${totalFemales}-${totalPuppies}-${totalVeterans}`;
      } catch (error) {
        console.error('Error calculating AKC conformation counts display:', error);
        // Fall back to regular entry count on error
        return `${getJudgeEntryCount(judge)} Entries`;
      }
    }
    
    // For UKC Conformation, calculate total entries from counts
    if (event.sanctioning_club === Constants.sanctioning_club.UKC && 
        propShow.show_type === Constants.show_type.conformation) {
      
      if (!ukcConformationCounts || ukcConformationCounts.size === 0) {
    return `${getJudgeEntryCount(judge)} Entries`;
      }
      
      let totalEntries = 0;
      // Only count breed groups that belong to THIS judge
      judge.dog_classes.forEach((dogClass) => {
        // Skip breaks
        if (dogClass.type === Constants.running_order_dog_class_type.break) {
          return;
        }
        if (dogClass.show_element === Constants.dog_class_element.junior_showmanship) {
          totalEntries += dogClass.entries || 0;
          return;
        }
        
        if (dogClass.level) {
          const breedGroup = dogClass.level;
          
          // Sum all breeds within this breed group (filtered by alpha-split range when present)
          ukcConformationCounts.forEach((counts, key) => {
            const [keyBreedGroup] = key.split('-');
            if (keyBreedGroup === breedGroup) {
              if (dogClass.start_letter && dogClass.end_letter) {
                const breed = key.substring(keyBreedGroup.length + 1);
                const first = breed[0]?.toUpperCase() ?? 'A';
                if (first < dogClass.start_letter || first > dogClass.end_letter) return;
              }
              const breedEntries = counts.male_count + counts.female_count + counts.champion_count + 
                             counts.grand_champion_count + counts.novice_puppy_count + counts.brace_count + 
                             counts.veteran_count + counts.alt_male_count + counts.alt_female_count + 
                             counts.alt_champion_count + counts.alt_grand_champion_count + 
                             counts.alt_novice_puppy_count + counts.alt_brace_count + counts.alt_veteran_count;
              totalEntries += breedEntries;
            }
          });
        }
      });
      
      return `${totalEntries} ${totalEntries === 1 ? 'Entry' : 'Entries'}`;
    }
    
    // For non-Conformation, show regular entry count
    return `${getJudgeEntryCount(judge)} Entries`;
  }, [event.sanctioning_club, propShow.show_type, akcConformationCounts, akcSpecialtyCounts, ukcConformationCounts, isSpecialtyShow, getJudgeEntryCount]);

  const getShowElementIndex = (_show_element: IRunningOrderDogClassDrag) => {
    let index:number = 0;
    for (let j in judges) {
      for (let se in judges[j].dog_classes) {
        if (_show_element === judges[j].dog_classes[se]) {
          return index;
        }

        index++;
      }
    }

    return index;
  }

  const resetDraggableIndexes = (_judges: IRunningOrderDrag[]) => {
    let _newJudges:IRunningOrderDrag[] = Array.from(_judges);
    let index:number = 0;

    for (let j in _newJudges) {
      for (let se in _newJudges[j].dog_classes) {
        _newJudges[j].dog_classes[se].index = index;
        index++;
      }
    }

    return _newJudges;
  }

  const addBreak = (index:number) => {
    let _judges:any = Array.from(judges);

    _judges[index].dog_classes.push({
      index: 0,
      type: Constants.running_order_dog_class_type.break,
      duration: 60,
      total_minutes: 60, // Set total_minutes to match duration for consistency
      name: "Break" // Default break name
    });

    _judges = resetDraggableIndexes(_judges);

    setJudges(_judges);
    updateRunningOrder(undefined, _judges, true); // Skip reload for break operations
  }

  const removeBreak = (jIndex: number, bIndex: number) => {
    let _judges:any = Array.from(judges);
    const removed = _judges[jIndex].dog_classes[bIndex];

    _judges[jIndex].dog_classes.splice(bIndex, 1);

    _judges = resetDraggableIndexes(_judges);

    setJudges(_judges);

    if (isUKCConformationBISTile(removed)) {
      // A BIS block is re-added by the schedule sync unless the show remembers it was hidden,
      // so save the ring and the hidden list together in one show update.
      const hidden = Array.from(new Set([...(showRef.current.hidden_bis_classes || []), removed.bis_class as string]));
      const updatedShow: IShow = {
        ...showRef.current,
        hidden_bis_classes: hidden,
        rings: (showRef.current.rings || []).map((ring: any) =>
          ring.ring !== ringName.id ? ring : { ...ring, running_order: _judges }
        ),
      };
      showRef.current = updatedShow;
      setShow(updatedShow);
      saveJudgeSchedule(updatedShow, undefined, true);
      return;
    }

    updateRunningOrder(undefined, _judges, true); // Skip reload for break operations
  }

  const changeBreakLength = (jIndex: number, bIndex: number, duration: number) => {
    let _judges:any = Array.from(judges);
    let oldDuration:number = Number(_judges[jIndex].dog_classes[bIndex].duration);

    _judges[jIndex].dog_classes[bIndex].duration = duration;
    // For breaks, also update total_minutes to match duration so it saves correctly
    _judges[jIndex].dog_classes[bIndex].total_minutes = duration;
    
    setJudges(_judges);

    // Always update running order when duration changes
    if (oldDuration !== duration) {
      updateRunningOrder(undefined, _judges, true); // Skip reload for break operations
    }
  }

  const updateBreakNameLocal = (jIndex: number, bIndex: number, name: string) => {
    let _judges:any = Array.from(judges);
    _judges[jIndex].dog_classes[bIndex].name = name;
    setJudges(_judges);
    
    // Only mark as unsaved if name is different from default "Break" (don't auto-save on every keystroke)
    if (name.trim() !== "Break" && name.trim() !== "") {
      setHasUnsavedBreakChanges(true);
    }
  }

  const saveBreakName = (jIndex: number, bIndex: number) => {

    
    // Only save if there are actual unsaved changes
    if (hasUnsavedBreakChanges) {
      // Update the show state with the current judges data before saving
      const updatedShow = { ...show };
      const currentRing = updatedShow.rings?.find((ring: any) => ring.ring === ringName.id);
      if (currentRing) {
        currentRing.running_order = judges;

        updateRunningOrder(updatedShow, undefined, true); // Pass the updated show
      } else {
        updateRunningOrder(undefined, judges, true); // Fallback
      }
      setHasUnsavedBreakChanges(false); // Clear unsaved changes flag
    }
  }

  // Simple save function with retry logic for write conflicts
  const saveJudgeSchedule = useCallback(async (_show?: any, _judges?: any, skipReload?: boolean, retryCount: number = 0, _baseShow?: any, _ringId?: string) => {
    const saveKey = `${event._id}-${propShow._id}`;
    try {
      let response: any;

      if (_show === undefined) {
        // --- Ring running-order save (drag / debounce) ---
        // Use a targeted endpoint that fetches fresh from DB and only patches this ring.
        // This avoids race conditions and full-show validation failures entirely.
        const targetRingId = _ringId ?? ringName.id;
        // Without a snapshot, judgesRef holds whatever ring is on screen now, which is
        // not the ring this save was queued for.
        if (_judges === undefined && targetRingId !== ringName.id) return;
        const newJudges: any[] = _judges ?? judgesRef.current;

        // Merge with the last-known ring state to preserve dog_run_order arrays.
        const existingRunningOrder: any[] = showRef.current?.rings
          ?.find((r: any) => r.ring === targetRingId)?.running_order ?? [];

        const mergedRunningOrder = newJudges.map((newJudge: any) => {
          const existingJudge = existingRunningOrder.find((existing: any) =>
            existing.judge_name === newJudge.judge_name && existing.judge_number === newJudge.judge_number
          );

          if (existingJudge?.dog_classes) {
            const mergedDogClasses = newJudge.dog_classes.map((newClass: any) => {
              const existingClass = existingJudge.dog_classes.find((existing: any) =>
                existing.show_element === newClass.show_element &&
                existing.level === newClass.level &&
                existing.sex_split === newClass.sex_split &&
                existing.variation === newClass.variation &&
                // Must also match split_number so sibling splits don't inherit each other's
                // dog_run_order. Treat undefined and null as equivalent (unsplit tiles).
                (existing.split_number ?? null) === (newClass.split_number ?? null)
              );
              if (existingClass) {
                const isSplitTile = newClass.split_number !== undefined;
                // For split tiles: prefer the DB dog_run_order because the user may have
                // moved dogs in RunOrderTab (which saved to DB) while judgesRef.current is
                // still at the original split state. Also prefer DB entries/total_minutes
                // which were updated by saveRunOrder.
                // For non-split tiles: prefer the DB order to preserve manual reordering.
                const useExistingOrder = existingClass.dog_run_order?.length > 0;
                return {
                  ...newClass,
                  dog_run_order: (useExistingOrder ? existingClass.dog_run_order : newClass.dog_run_order) || [],
                  // For split tiles that have DB-updated entries/total_minutes, use those.
                  entries: isSplitTile && existingClass.entries !== undefined
                    ? existingClass.entries
                    : newClass.entries,
                  total_minutes: isSplitTile && existingClass.total_minutes !== undefined
                    ? existingClass.total_minutes
                    : newClass.total_minutes,
                  group_order: newClass.group_order !== undefined ? newClass.group_order : existingClass.group_order,
                };
              }
              return newClass;
            });
            return { ...newJudge, dog_classes: mergedDogClasses };
          }
          return newJudge;
        });

        // Preserve any judges that exist in the DB but are NOT in the current
        // snapshot (e.g., a judge was added after the last drag captured _judges).
        // Without this, a stale debounced save would silently drop those judges.
        const snapshotJudgeKeys = new Set(
          newJudges.map((j: any) => `${j.judge_name}|${j.judge_number || ''}`)
        );
        const extraDbJudges = existingRunningOrder.filter(
          (existing: any) => !snapshotJudgeKeys.has(`${existing.judge_name}|${existing.judge_number || ''}`)
        );
        mergedRunningOrder.push(...extraDbJudges);

        const apiCall = apiService.updateRingRunningOrder(event._id, propShow._id, targetRingId, mergedRunningOrder);
        pendingShowSaves.set(saveKey, apiCall.catch(() => {}));
        response = await apiCall;
        pendingShowSaves.delete(saveKey);

      } else {
        // --- Full show update (settings change, etc.) ---
        // _baseShow: explicit base to merge against (used in write-conflict retries).
        let _updatedShow: any = _baseShow !== undefined ? { ..._baseShow } : { ...showRef.current };

        if (_show.rings && _updatedShow.rings) {
          _updatedShow.rings = _updatedShow.rings.map((currentRing: any) => {
            const staleRing = (_show.rings as any[]).find((r: any) => r.ring === currentRing.ring);
            if (!staleRing) return currentRing;
            const staleJudgeKeys = new Set(
              (staleRing.running_order || []).map((ro: any) => `${ro.judge_name}|${ro.judge_number || ''}`)
            );
            const extraJudges = (currentRing.running_order || []).filter(
              (ro: any) => !staleJudgeKeys.has(`${ro.judge_name}|${ro.judge_number || ''}`)
            );
            return {
              ...staleRing,
              running_order: [...(staleRing.running_order || []), ...extraJudges],
            };
          });
        }

        delete _updatedShow.__v;
        delete _updatedShow.updated_at;
        delete _updatedShow.created_at;

        const apiCall = apiService.updateShow(event._id, propShow._id, _updatedShow);
        pendingShowSaves.set(saveKey, apiCall.catch(() => {}));
        response = await apiCall;
        pendingShowSaves.delete(saveKey);
      }

      // Notify parent component that show data has changed
      onUpdate(response);

      // Update local show state with the server response
      setShow(response);

      // Only reload if not explicitly skipped (for drag operations, we don't want to reload)
      if (!skipReload) {
        setLoaded(false);
      }
      
      // Notify Run Order tab that changes are pending (for manual sync button)
      if (onJudgeScheduleChange) {
        onJudgeScheduleChange();
      }

    } catch (error: any) {
      pendingShowSaves.delete(saveKey);
      // Check if this is a write conflict error
      const isWriteConflict = error.response?.data?.message?.includes('Write conflict') || 
                              error.response?.data?.message?.includes('yielding is disabled');
      
      // Retry up to 3 times with exponential backoff for write conflicts
      if (isWriteConflict && retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 100; // 100ms, 200ms, 400ms
        
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Fetch the latest show data before retrying
        try {
          const allShows = await apiService.getClubEventShows(event.club_id, event._id);
          const latestShow = allShows.find(s => s._id === propShow._id);
          
          if (latestShow) {
            setShow(latestShow);
            
            // Retry the save: use latestShow as the merge base (_baseShow) so we get a
            // fresh __v, but keep _judges as the client state so drag/edit changes are
            // preserved through the merge rather than silently overwritten.
            return saveJudgeSchedule(undefined, _judges, skipReload, retryCount + 1, latestShow, _ringId);
          }
        } catch (fetchError) {
          console.error('Failed to fetch latest show data for retry:', fetchError);
        }
      }
      
      // Only show toast if we've exhausted retries or it's not a write conflict
      if (!isWriteConflict || retryCount >= 3) {
        toast.show({
          title: "Judge Schedules",
          content: error.response?.data?.message || "Something went wrong, please try again later",
          duration: 10000,
          type: "fail"
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, propShow._id, ringName.id, onUpdate, onJudgeScheduleChange]);

  // Keep a ref to the latest saveJudgeSchedule so the stable debounce below always
  // calls the current version without needing to be recreated.
  const saveJudgeScheduleRef = useRef(saveJudgeSchedule);
  useEffect(() => { saveJudgeScheduleRef.current = saveJudgeSchedule; }, [saveJudgeSchedule]);

  // Stable debounced save — created once for the lifetime of the component so pending
  // timers are never orphaned when saveJudgeSchedule or its dependencies change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useMemo(
    () => debounce((...args: Parameters<typeof saveJudgeSchedule>) => saveJudgeScheduleRef.current(...args), 1500),
    [] // intentionally empty — must never be recreated
  );

  const updateRunningOrder = useCallback((_show?: any, _judges?: any, skipReload?: boolean) => {
    debouncedSave(_show, _judges, skipReload, 0, undefined, currentRingIdRef.current);
  }, [debouncedSave]);

  const saveAllUnsavedBreakChanges = useCallback(() => {
    if (hasUnsavedBreakChanges) {
      // Update the show state with current judges data before saving
      const updatedShow = { ...showRef.current };
      const currentRing = updatedShow.rings?.find((ring: any) => ring.ring === ringName.id);
      if (currentRing) {
        currentRing.running_order = judges;
        updateRunningOrder(updatedShow, undefined, true);
      } else {
        updateRunningOrder(undefined, judges, true); // Fallback
      }
      setHasUnsavedBreakChanges(false);
    }
  }, [hasUnsavedBreakChanges, ringName.id, judges, updateRunningOrder]);

  // Grouping functions
  const toggleGroupingMode = () => {
    setGroupingMode(!groupingMode);
    setSelectedForGrouping([]);
  };

  const toggleLevelSelection = (judgeIndex: number, levelIndex: number, level: IRunningOrderDogClassDrag) => {
    if (!groupingMode || level.type === Constants.running_order_dog_class_type.break) return;
    
    const levelKey = `${level.show_element}-${level.level}`;
    const selectionKey = `${judgeIndex}-${levelIndex}-${levelKey}`;
    
    setSelectedForGrouping(prev => {
      const wasSelected = prev.includes(selectionKey);
      const newSelection = wasSelected 
        ? prev.filter(key => key !== selectionKey)
        : [...prev, selectionKey];
      
      return newSelection;
    });
  };

  const groupSelectedLevels = () => {
    if (selectedForGrouping.length < 2) {
      toast.show({
        title: "Group Levels",
        content: "Please select at least 2 levels to group together",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    // Use the first selected level as the parent (main) level
    const [firstSelection, ...childSelections] = selectedForGrouping;
    const firstLevelKey = firstSelection.split('-').slice(2).join('-'); // Extract level key from selection key
    const parentShowElement = firstLevelKey.split('-')[0]; // Extract show_element
    const parentLevel = firstLevelKey.split('-')[1]; // Extract level name
    
    // Apply grouping immediately to the judges data
    let updatedJudges = [...judges];
    let groupedCount = 0;
    
    // Set group_with property on child levels and update parent level with merged data
    childSelections.forEach((selectionKey, index) => {
      const levelKey = selectionKey.split('-').slice(2).join('-');
      const childShowElement = levelKey.split('-')[0]; // Extract show_element
      const childLevel = levelKey.split('-')[1]; // Extract level name
      
      // Find and update the child level to group with parent
      updatedJudges = updatedJudges.map(judge => ({
        ...judge,
        dog_classes: judge.dog_classes.map(dogClass => {
          if (dogClass.show_element === childShowElement && dogClass.level === childLevel) {
            groupedCount++;
            return {
              ...dogClass,
              group_with: parentLevel, // Use simple parent level name (OLD format - backwards compatible)
              group_order: index // Store the selection order (0-based index)
            };
          }
          return dogClass;
        })
      }));
    });
    
    // Update parent level with merged data from grouped children
    updatedJudges = updatedJudges.map(judge => ({
      ...judge,
      dog_classes: judge.dog_classes.map(dogClass => {
        if (dogClass.show_element === parentShowElement && dogClass.level === parentLevel) {
          // Find all children that are now grouped with this parent (OLD format - simple name)
          const children = judge.dog_classes.filter(dc => 
            dc.group_with === parentLevel && dc.show_element === parentShowElement
          );
          
          // Calculate merged entries and timing FROM child classes
          const totalChildEntries = children.reduce((sum, child) => sum + (child.entries || 0), 0);
          const totalChildMinutes = children.reduce((sum, child) => sum + (child.total_minutes || 0), 0);
          
          // Build label showing grouped levels
          const childLevels = children.map(child => `(${child.level})`).join(' ');
          const existingLabel = dogClass.label || "";
          const newLabel = existingLabel ? `${existingLabel} ${childLevels}` : childLevels;
          
          return {
            ...dogClass,
            entries: (dogClass.entries || 0) + totalChildEntries,
            total_minutes: (dogClass.total_minutes || 0) + totalChildMinutes,
            label: newLabel
          };
        }
        return dogClass;
      })
    }));
    
    // Update state and save to backend immediately
    setJudges(updatedJudges);
    
    // Update the levelGroups Map for frontend state consistency
    const newGroups = new Map(levelGroups);
    childSelections.forEach(selectionKey => {
      const levelKey = selectionKey.split('-').slice(2).join('-');
      newGroups.set(levelKey, firstLevelKey);
    });
    setLevelGroups(newGroups);
    
    // Save the grouping changes to backend immediately
    updateRunningOrder(undefined, updatedJudges, true);
    
    setSelectedForGrouping([]);
    setGroupingMode(false);
    
    toast.show({
      title: "Group Levels",
      content: `Successfully grouped ${groupedCount} level(s) with ${parentLevel}`,
      duration: 5000,
      type: "success"
    });
  };

  const reverseRunningOrder = () => {
    if (judges.length === 0) return;

    // Create a copy of judges array
    let reversedJudges = [...judges];
    
    // For each judge, reverse their dog_classes array
    reversedJudges = reversedJudges.map(judge => ({
      ...judge,
      dog_classes: [...judge.dog_classes].reverse()
    }));
    
    // Reset draggable indexes after reversing
    reversedJudges = resetDraggableIndexes(reversedJudges);
    
    // Update state and save to backend
    setJudges(reversedJudges);
    updateRunningOrder(undefined, reversedJudges, true);
    
    toast.show({
      title: "Reverse Order",
      content: "Successfully reversed the running order for all judges",
      duration: 5000,
      type: "success"
    });
  };

  // Helper function to regenerate rings with default configuration
  const regenerateRings = () => {
    const rings: any[] = [];
    
    // Get all show elements that are selected
    const selectedElements = propShow.show_elements || [];
    
    if (selectedElements.length === 0) {
      return rings;
    }
    
    // Check if this is an AKC Conformation specialty show
    const isAKCConformationSpecialty = event.sanctioning_club === Constants.sanctioning_club.AKC &&
                                       propShow.show_type === Constants.show_type.conformation &&
                                       isSpecialtyShow;
    
    // Create running order based on judges
    const runningOrder: any[] = [];
    
    // Get unique judges from show elements
    const judgeMap = new Map<string, any>();
    
    selectedElements.forEach((se: any) => {
      if (se.judge_name && se.judge_number) {
        const judgeKey = `${se.judge_name}-${se.judge_number}`;
        if (!judgeMap.has(judgeKey)) {
          judgeMap.set(judgeKey, {
            judge_name: se.judge_name,
            judge_number: se.judge_number,
            dog_classes: []
          });
        }
      }
    });
    
    // If no judges found in show_elements, create a default structure
    if (judgeMap.size === 0) {
      runningOrder.push({
        judge_name: "",
        judge_number: "",
        dog_classes: []
      });
    } else {
      judgeMap.forEach((value) => runningOrder.push(value));
    }
    
    // Add dog classes to the appropriate judge's running order
    // Use a Set to track added levels per judge to prevent duplicates
    // Deduplicate by LEVEL only (not show_element+level) because the same level 
    // might appear with different show_elements but should only appear once after sex split
    const addedLevelsPerJudge = new Map<string, Set<string>>();
    
    selectedElements.forEach((se: any) => {
      // Find the judge for this element
      const judgeKey = se.judge_name && se.judge_number 
        ? `${se.judge_name}-${se.judge_number}` 
        : 'default';
      
      const targetJudge = judgeKey !== 'default'
        ? runningOrder.find(ro => `${ro.judge_name}-${ro.judge_number}` === judgeKey)
        : runningOrder[0];
      
      if (targetJudge) {
        // Use element+level+section as the unique key to avoid deduplication collisions
        // (e.g. ABI SDG has empty level but distinct show_element+section combinations)
        const levelKey = `${se.show_element || ''}-${se.level || ''}-${se.section || ''}`;
        
        // Track added levels for this judge
        if (!addedLevelsPerJudge.has(judgeKey)) {
          addedLevelsPerJudge.set(judgeKey, new Set());
        }
        
        // Only add if this combination not already added for this judge
        if (!addedLevelsPerJudge.get(judgeKey)!.has(levelKey)) {
          addedLevelsPerJudge.get(judgeKey)!.add(levelKey);
          
          const dogClass: any = {
            type: Constants.running_order_dog_class_type.dog_class,
            show_element: se.show_element || propShow.show_type,
            level: se.level
          };

          // Preserve section for show types that use it (ABI SDG, C-WAGS, etc.)
          if (se.section) {
            dogClass.section = se.section;
          }
          
          targetJudge.dog_classes.push(dogClass);
        }
      }
    });
    
    // For AKC Conformation specialty shows, sort and apply sex split
    if (isAKCConformationSpecialty && akcSpecialtyCounts.size > 0) {
      const runningTimePerDog = getRunningTimeMinutes(
        event.sanctioning_club,
        propShow.show_type,
        undefined,
        undefined
      ) || 2;
      
      runningOrder.forEach((ro) => {
        // Sort by catalog order first
        ro.dog_classes.sort((a: any, b: any) => {
          return getSpecialtyClassOrderIndex(a.level || '') - getSpecialtyClassOrderIndex(b.level || '');
        });
        
        // Separate into splittable and non-splittable classes
        const splittableClasses = ro.dog_classes.filter((dc: any) => 
          dc.type === Constants.running_order_dog_class_type.dog_class && 
          shouldSplitBySex(dc.level || '')
        );
        const nonSplittableClasses = ro.dog_classes.filter((dc: any) => 
          dc.type !== Constants.running_order_dog_class_type.dog_class || 
          !shouldSplitBySex(dc.level || '')
        );
        
        // Group by section: Sweepstakes, Regular, Veteran
        const sweepstakesClasses = splittableClasses.filter((dc: any) => 
          AKC_SPECIALTY_SWEEPSTAKES_LEVELS.some(l => (dc.level || '').toLowerCase() === l.toLowerCase())
        );
        const regularClasses = splittableClasses.filter((dc: any) => 
          AKC_SPECIALTY_REGULAR_LEVELS.some(l => (dc.level || '').toLowerCase() === l.toLowerCase())
        );
        const veteranClasses = splittableClasses.filter((dc: any) => 
          AKC_SPECIALTY_VETERAN_LEVELS.some(l => (dc.level || '').toLowerCase() === l.toLowerCase())
        );
        // Catch-all for classes that don't match any category
        const otherClasses = splittableClasses.filter((dc: any) => 
          !sweepstakesClasses.includes(dc) &&
          !regularClasses.includes(dc) &&
          !veteranClasses.includes(dc)
        );
        
        const newDogClasses: any[] = [];
        
        // Helper to create split classes for Dogs then Bitches within a category
        const createSplitClasses = (classes: any[]) => {
          // Dogs first
          classes.forEach((dc: any) => {
            const key = `${ro.judge_name}-${dc.level}`;
            const counts = akcSpecialtyCounts.get(key);
            const maleCount = counts?.males || 0;
            
            if (maleCount > 0) {
              newDogClasses.push({
                ...dc,
                sex_split: "Dogs",
                entries: maleCount,
                total_minutes: maleCount * runningTimePerDog,
                duration: runningTimePerDog
              });
            }
          });
          
          // Then Bitches
          classes.forEach((dc: any) => {
            const key = `${ro.judge_name}-${dc.level}`;
            const counts = akcSpecialtyCounts.get(key);
            const femaleCount = counts?.females || 0;
            
            if (femaleCount > 0) {
              newDogClasses.push({
                ...dc,
                sex_split: "Bitches",
                entries: femaleCount,
                total_minutes: femaleCount * runningTimePerDog,
                duration: runningTimePerDog
              });
            }
          });
        };
        
        // Apply in order: Sweepstakes, Regular, Veteran, Other
        createSplitClasses(sweepstakesClasses);
        createSplitClasses(regularClasses);
        createSplitClasses(veteranClasses);
        createSplitClasses(otherClasses);
        
        // Add non-splittable classes at the end (Best of Breed, Junior Showmanship)
        nonSplittableClasses.forEach((dc: any) => {
          if (dc.type === Constants.running_order_dog_class_type.dog_class) {
            const key = `${ro.judge_name}-${dc.level}`;
            const counts = akcSpecialtyCounts.get(key);
            const totalCount = (counts?.males || 0) + (counts?.females || 0);
            
            newDogClasses.push({
              ...dc,
              entries: totalCount,
              total_minutes: totalCount * runningTimePerDog,
              duration: runningTimePerDog
            });
          } else {
            newDogClasses.push(dc);
          }
        });
        
        ro.dog_classes = newDogClasses;
      });
      
      // Mark as initialized so useEffect doesn't re-apply
      akcSpecialtySexSplitInitializedRef.current = true;
    } else if (isAKCConformationSpecialty) {
      // Sort only if no counts available yet
      runningOrder.forEach((ro) => {
        ro.dog_classes.sort((a: any, b: any) => {
          return getSpecialtyClassOrderIndex(a.level || '') - getSpecialtyClassOrderIndex(b.level || '');
        });
      });
    }
    
    // Create the ring
    rings.push({
      ring: Constants.ring_names.ring_1,
      running_order: runningOrder
    });
    
    return rings;
  };

  const resetSchedule = async () => {
    const hasSplitsNow = anyClassHasSplits(judges);
    const msg = hasSplitsNow
      ? "Resetting the judge schedule will remove all class splits, custom breaks, and manual ordering changes. Continue?"
      : "Resetting the judge schedule can overwrite custom breaks and manual class ordering changes. Continue?";
    const confirmed = window.confirm(msg);
    if (!confirmed) {
      return;
    }

    try {
      // Use the backend sync API to regenerate rings from actual scores
      // This ensures Junior Showmanship and other classes are included even if not in show_elements
      const syncedShow = await apiService.syncShowRingsWithScores(event._id, propShow._id);

      // Update local state
      setShow(syncedShow);

      // Notify parent component
      onUpdate(syncedShow);

      // Reset initialization flags
      ukcConformationInitializedRef.current = false;
      akcAgilityInitializedRef.current = false;
      akcSpecialtySexSplitInitializedRef.current = false;

      // Trigger reload to pick up the synced rings
      setLoaded(false);
      
      toast.show({
        title: "Judge Schedules",
        content: "Schedule reset successfully with default times.",
        duration: 4000,
        type: "success"
      });
    } catch (error: any) {
      toast.show({
        title: "Judge Schedules",
        content: error.response?.data?.message || "Failed to reset schedule",
        duration: 10000,
        type: "fail"
      });
    }
  };

  const ungroupLevel = (level: IRunningOrderDogClassDrag) => {

    
    const levelKey = `${level.show_element}-${level.level}`;
    let wasUngrouped = false;
    let ungroupedCount = 0;
    
    // Handle dynamically grouped levels (this level is grouped WITH another)
    if (levelGroups.has(levelKey)) {
      const newGroups = new Map(levelGroups);
      newGroups.delete(levelKey);
      setLevelGroups(newGroups);
      wasUngrouped = true;
      ungroupedCount++;

    }
    
    // Handle dynamic parent levels (other levels are grouped WITH this level)
    const childrenToUngroup: string[] = [];
    levelGroups.forEach((parentKey, childKey) => {
      if (parentKey === levelKey) {
        childrenToUngroup.push(childKey);
      }
    });
    
    if (childrenToUngroup.length > 0) {
      const newGroups = new Map(levelGroups);
      childrenToUngroup.forEach(childKey => {
        newGroups.delete(childKey);
        ungroupedCount++;
      });
      setLevelGroups(newGroups);
      wasUngrouped = true;

    }
    
    // Start with current judges for any modifications
    let currentJudges = [...judges];
    
    // Handle legacy grouped levels (this level has group_with property)
    if (level.group_with) {
      currentJudges = currentJudges.map(judge => ({
        ...judge,
        dog_classes: judge.dog_classes.map(dogClass => {
          if (dogClass === level) {
            const { group_with, group_order, label, ...rest } = dogClass;

            ungroupedCount++;
            return rest;
          }
          return dogClass;
        })
      }));
      
      wasUngrouped = true;

    }
    
    // Handle legacy parent levels (find children with group_with pointing to this level)
    let childrenFound: IRunningOrderDogClassDrag[] = [];
    
    // Build parent identifiers for both grouping formats:
    // NEW format: "show_element:level"
    // OLD format: simple level name scoped by show_element
    const parentIdentifier = `${level.show_element}:${level.level}`;
    const parentLegacyLevel = level.level;
    
    let finalUpdatedJudges = currentJudges.map(judge => ({
      ...judge,
      dog_classes: judge.dog_classes.map(dogClass => {
        const matchesNewFormat = dogClass.group_with === parentIdentifier;
        const matchesLegacyFormat = dogClass.group_with === parentLegacyLevel && dogClass.show_element === level.show_element;

        if (matchesNewFormat || matchesLegacyFormat) {
          const { group_with, group_order, label, ...rest } = dogClass;

          childrenFound.push(dogClass); // Store child info for parent cleanup
          ungroupedCount++;
          wasUngrouped = true;
          return rest;
        }
        return dogClass;
      })
    }));
    
    // Clean up the parent level - remove merged data from children
    if (childrenFound.length > 0) {
      finalUpdatedJudges = finalUpdatedJudges.map(judge => ({
        ...judge,
        dog_classes: judge.dog_classes.map(dogClass => {
          if (dogClass === level) {
            // Calculate what the parent level should be without the merged children
            let cleanedLevel = { ...dogClass };
            
            // Subtract entries from grouped children
            childrenFound.forEach(child => {
              cleanedLevel.entries = (cleanedLevel.entries || 0) - (child.entries || 0);
              cleanedLevel.total_minutes = (cleanedLevel.total_minutes || 0) - (child.total_minutes || 0);
            });
            
            // Remove the grouped label if it exists
            if ('label' in cleanedLevel) {
              const { label, ...rest } = cleanedLevel as any;

              return rest;
            } else {

              return cleanedLevel;
            }
          }
          return dogClass;
        })
      }));
    }
    
    if (wasUngrouped) {
      // Update the judges state and save to server
      setJudges(finalUpdatedJudges);
      
      // Save the ungrouped data to the server (skip reload to preserve local modifications)
      updateRunningOrder(undefined, finalUpdatedJudges, true);
      
      toast.show({
        title: "Ungroup Level",
        content: `Successfully ungrouped ${ungroupedCount} level(s) from ${level.level}`,
        duration: 5000,
        type: "success"
      });
    } else {

      toast.show({
        title: "Ungroup Level",
        content: `Could not ungroup ${level.level} - it may not be grouped`,
        duration: 5000,
        type: "fail"
      });
    }
  };

  const isLevelGrouped = (level: IRunningOrderDogClassDrag): boolean => {
    const levelKey = `${level.show_element}-${level.level}`;
    return level.group_with !== undefined || levelGroups.has(levelKey);
  };

  const getGroupedLevels = (parentLevel: IRunningOrderDogClassDrag): string[] => {
    const parentKey = `${parentLevel.show_element}-${parentLevel.level}`;
    const grouped: string[] = [];
    
    // Check for levels grouped with this parent (NEW format)
    levelGroups.forEach((parentLevelKey, childLevelKey) => {
      if (parentLevelKey === parentKey) {
        const levelName = childLevelKey.split('-')[1];
        grouped.push(levelName);
      }
    });
    
    // Check for OLD format: find all dog_classes that have group_with pointing to this parent's level
    const parentLevelName = parentLevel.level;
    judges.forEach(judge => {
      judge.dog_classes.forEach((dc: IRunningOrderDogClassDrag) => {
        if (dc.group_with === parentLevelName && dc.show_element === parentLevel.show_element && dc.level) {
          if (!grouped.includes(dc.level)) {
            grouped.push(dc.level);
          }
        }
      });
    });
    
    // Also check for legacy grouped levels in label
    if (parentLevel.label) {
      const matches = parentLevel.label.match(/\(([^)]+)\)/g);
      if (matches) {
        matches.forEach(match => {
          const levelName = match.slice(1, -1); // Remove parentheses
          if (!grouped.includes(levelName)) {
            grouped.push(levelName);
          }
        });
      }
    }
    
    return grouped;
  };

  const onDragEnd = useCallback((result: any) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    const sInd = +source.droppableId;
    const dInd = +destination.droppableId;

    let _judges = Array.from(judges);

    // Get the filtered list for the source judge (must match the render filter exactly)
    const sourceJudgeFilteredClasses = _judges[source.droppableId].dog_classes.filter((se: IRunningOrderDogClassDrag) => {
      if (se.type === Constants.running_order_dog_class_type.break) {
        return true;
      }
      if (!se.level) {
        return false; // exclude placeholder rows (same as render filter)
      }
      if (!isScentworkClassOffered(propShow, se)) {
        return false;
      }
      // Respect the "Hide empty elements and levels" toggle uniformly for all judges
      return !hideEmptyClasses || (se.entries && se.entries > 0);
    });
    
    // Get the actual dog_class being dragged from the filtered list
    const dogClassBeingDragged = sourceJudgeFilteredClasses[source.index];
    
    if (!dogClassBeingDragged) {
      console.error('Could not find dog class at source index:', source.index);
      return;
    }
    
    // Find the actual index in the unfiltered array
    const actualSourceIndex = _judges[source.droppableId].dog_classes.indexOf(dogClassBeingDragged);

    if (sInd === dInd) {
      // Reordering within same judge - map visual indices to actual indices
      const destFilteredClasses = _judges[sInd].dog_classes.filter((se: IRunningOrderDogClassDrag) => {
        if (se.type === Constants.running_order_dog_class_type.break) {
          return true;
        }
        if (!se.level) {
          return false; // exclude placeholder rows (same as render filter)
        }
        if (!isScentworkClassOffered(propShow, se)) {
          return false;
        }
        return !hideEmptyClasses || (se.entries && se.entries > 0);
      });
      
      const actualDestIndex = destFilteredClasses[destination.index] 
        ? _judges[sInd].dog_classes.indexOf(destFilteredClasses[destination.index])
        : _judges[sInd].dog_classes.length;
      
      // Simple reorder without offset - we already have the actual indices
      const [removed] = _judges[sInd].dog_classes.splice(actualSourceIndex, 1);
      _judges[sInd].dog_classes.splice(actualDestIndex, 0, removed);
      
      // Reset the draggable indices after reordering
      _judges = resetDraggableIndexes(_judges);
      
      // Invalidate any in-flight async load so it cannot overwrite this drag result
      runningOrderLoadVersionRef.current++;
      setJudges(_judges);
      // Save immediately (cancel any pending debounce so there's only one in-flight save)
      debouncedSave.cancel();
      saveJudgeScheduleRef.current(undefined, _judges, true);
    } else {
      if (dogClassBeingDragged.type !== Constants.running_order_dog_class_type.break) {
        // Prevent moving Junior Showmanship classes between judges
        // Junior Showmanship entries are tied to specific judges in registration data
        if (dogClassBeingDragged.show_element === Constants.dog_class_element.junior_showmanship) {
          toast.show({
            title: "Junior Showmanship",
            content: "Junior Showmanship classes cannot be moved between judges as they are tied to specific judge assignments in the registrations.",
            duration: 10000,
            type: "fail"
          });
          return;
        }
        
        // Map destination index from visual to actual (must match render filter exactly)
        const destFilteredClasses = _judges[dInd].dog_classes.filter((se: IRunningOrderDogClassDrag) => {
          if (se.type === Constants.running_order_dog_class_type.break) {
            return true;
          }
          if (!se.level) {
            return false; // exclude placeholder rows (same as render filter)
          }
          if (!isScentworkClassOffered(propShow, se)) {
            return false;
          }
          return !hideEmptyClasses || (se.entries && se.entries > 0);
        });
        
        const actualDestIndex = destFilteredClasses[destination.index]
          ? _judges[dInd].dog_classes.indexOf(destFilteredClasses[destination.index])
          : _judges[dInd].dog_classes.length;
        
        // Simple move without offset - we already have the actual indices
        const sourceJudge = _judges[sInd];
        const [removed] = _judges[sInd].dog_classes.splice(actualSourceIndex, 1);
        _judges[dInd].dog_classes.splice(actualDestIndex, 0, removed);

        // Remove any zeroed-out duplicate of the same class that may already exist
        // in the destination judge (e.g. from a previous Reset that zeroed but didn't
        // remove the class). Without this, the dragged class gets inserted alongside
        // the zeroed one, producing two entries for the same level.
        const dupeIndex = _judges[dInd].dog_classes.findIndex((dc: any) =>
          dc !== removed &&
          dc.show_element === removed.show_element &&
          dc.level === removed.level &&
          (dc.entries || 0) === 0
        );
        if (dupeIndex !== -1) {
          _judges[dInd].dog_classes.splice(dupeIndex, 1);
        }
        
        // Update the score records with the new judge assignment.
        // This ensures drag changes persist when syncing or resetting.
        const destJudge = _judges[dInd];
        const scoreIds = removed.dog_run_order || [];
        
        const isSplitTile = (removed as any).split_number !== undefined;

        if (scoreIds.length > 0) {
          // Preferred path: update by specific score IDs (most precise)
          apiService.bulkUpdateScoreJudge(
            event._id,
            { _id: { $in: scoreIds } },
            { judge_name: destJudge.judge_name, judge_number: destJudge.judge_number || '' }
          ).catch((error) => {
            console.error('Failed to update score judge assignments:', error);
          });
        } else if (!isSplitTile && (removed.level || removed.show_element)) {
          // Fallback (non-split tiles only): dog_run_order is empty (class was never synced
          // after registration). Query by show + element + level + source judge_name.
          // We skip this for split tiles because filtering by level alone would update ALL
          // scores for the class (across all splits), not just the ones in this split.
          // For split tiles with no dog_run_order, the judge assignment on scores will be
          // updated once the user assigns dogs in RunOrderTab and saveRunOrder fires.
          const scoreFilter: any = {
            show_id: show._id,
            judge_name: sourceJudge.judge_name,
          };
          if (removed.show_element) scoreFilter['dog_class.show_element'] = removed.show_element;
          if (removed.level) scoreFilter['dog_class.level'] = removed.level;
          if ((removed as any).section) scoreFilter['dog_class.section'] = (removed as any).section;

          apiService.bulkUpdateScoreJudge(
            event._id,
            scoreFilter,
            { judge_name: destJudge.judge_name, judge_number: destJudge.judge_number || '' }
          ).catch((error) => {
            console.error('Failed to update score judge assignments (fallback):', error);
          });
        }

        // Keep show_elements (the "Judges" tab config) in sync with a full (non-split)
        // class move so NEW registrations for this class default to the new judge too,
        // instead of drifting back to whoever show_elements still says. Split tiles
        // intentionally share a class across judges via split_number/split_total, so
        // we leave show_elements alone for those.
        if (!isSplitTile && (removed.show_element || removed.level)) {
          apiService.updateShowElementJudge(
            event._id,
            propShow._id,
            {
              show_element: removed.show_element || '',
              level: removed.level,
              section: (removed as any).section,
              judge_name: destJudge.judge_name,
              judge_number: destJudge.judge_number || '',
            }
          ).catch((error) => {
            console.error('Failed to update show_elements judge assignment:', error);
          });
        }
        
        // Reset the draggable indices after moving
        _judges = resetDraggableIndexes(_judges);
        
        // Invalidate any in-flight async load so it cannot overwrite this drag result
        runningOrderLoadVersionRef.current++;
        setJudges(_judges);
        // Save immediately (cancel any pending debounce so there's only one in-flight save)
        debouncedSave.cancel();
        saveJudgeScheduleRef.current(undefined, _judges, true);
      } else {
        toast.show({
          title: "Judge Breaks",
          content: "To add a break to a different judge, click 'Add Break' beside the judges total hours",
          duration: 10000,
          type: "fail"
        });
      }
    }
  }, [judges, updateRunningOrder, hideEmptyClasses, resetDraggableIndexes, propShow]);

  const getTotalJudgeHours = (judge: IRunningOrderDrag, upToIndex?: number) => {
    // Calculate total minutes excluding hidden/grouped levels
    let totalMinutes = 0;
    
    // Handle cases where dog_classes might be empty or undefined (e.g., UKC Conformation)
    if (!judge.dog_classes || judge.dog_classes.length === 0) {
      return 0;
    }
    
    for (let i = 0; i < judge.dog_classes.length; i++) {
      if (upToIndex !== undefined && i >= upToIndex) break;
      
      const dogClass = judge.dog_classes[i];
      // Only count non-grouped levels
      if (dogClass && dogClass.group_with === undefined) {
        // Use duration first (works for breaks and conformation), fall back to total_minutes (for agility)
        totalMinutes += dogClass.duration || dogClass.total_minutes || 0;
      }
    }
    
    return Number((totalMinutes / 60).toFixed(2));
  }

  // Helper function to format minutes into "Xh Ym (Z.Zm)" format
  const formatMinutesDisplay = (minutes: number): string => {
    if (!minutes || minutes === 0) {
      return "0h 0m (0.0m)";
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.floor(minutes % 60);
    
    return `${hours}h ${remainingMinutes}m (${minutes.toFixed(1)}m)`;
  };

  const getTotalJudgeMinutes = useCallback((judge: IRunningOrderDrag) => {
    const judgeName = judge.judge_name?.trim();
    
    // Use the shared extractJudgeScheduleFromRings utility to match backend calculation exactly
    // This handles recalculation from scores when dog_run_order is empty, grouped classes, etc.
    const judgeSchedules = extractJudgeScheduleFromRings(show, undefined, allScores, event);
    const judgeSchedule = judgeSchedules.find(js => js.judgeName?.trim() === judgeName);
    
    // Use judgingMinutes (excludes breaks) for display
    if (judgeSchedule && judgeSchedule.judgingMinutes > 0) {
      return formatMinutesDisplay(judgeSchedule.judgingMinutes);
    }
    
    // Fallback: Calculate from dog_classes if no schedule found
    const totalMinutes = (judge.dog_classes || []).reduce((sum, dc) => {
      if (dc.type === Constants.running_order_dog_class_type.break) return sum;
      if (dc.group_with) return sum;
      return sum + (dc.total_minutes || 0);
    }, 0);
    
    return formatMinutesDisplay(totalMinutes);
  }, [show, allScores, event]);

  const getDailyJudgeMinutes = useCallback((judge: IRunningOrderDrag) => {
    // Calculate total minutes for this judge across ALL shows on the same date
    // Use extractJudgeScheduleFromRings so daily hours match capacity calculations
    const currentDate = moment(props.show.show_date).format('YYYY-MM-DD');
    const judgeName = judge.judge_name?.trim();
    
    let dailyTotalMinutes = 0;
    
    const showsOnDate = allShows.filter(s => 
      moment(s.show_date).format('YYYY-MM-DD') === currentDate
    );
    
    showsOnDate.forEach(showOnDate => {
      const isCurrentShow = showOnDate._id?.toString() === props.show._id?.toString();
      const showForCalc = isCurrentShow ? show : showOnDate;
      const showScores = allScores.filter(score => 
        score.show_id?.toString() === showOnDate._id?.toString()
      );
      
      const judgeSchedules = extractJudgeScheduleFromRings(showForCalc, undefined, showScores, event);
      const judgeSchedule = judgeSchedules.find(js => js.judgeName?.trim() === judgeName);
      
      if (judgeSchedule) {
        dailyTotalMinutes += judgeSchedule.judgingMinutes;
      }
    });
    
    return formatMinutesDisplay(dailyTotalMinutes);
  }, [props.show.show_date, props.show._id, allShows, allScores, show, event]);

  const moveJudge = (index: number, direction:"left" | "right") => {
    let _judges:IRunningOrderDrag[] = Array.from(judges);
    let _j:IRunningOrderDrag = _judges[index];

    _judges.splice(index, 1);
    _judges.splice(direction === "right" ? index + 1 : index -1, 0, _j);

    setJudges(_judges);
    updateRunningOrder(undefined, _judges);
  }

  // Get the show's base start time (accounting for trailing shows)
  const getShowStartTime = useCallback(() => {
    let baseStartTime = new Date(props.show.show_date);
    
    // Handle trailing shows - if this show follows another show, start after the previous show ends
    if (props.show.trailing_show && props.show.previous_show_name && allShows.length > 0) {
      // Try exact match first, then show_type + show_name combination
      let previousShow = allShows.find(s => s.show_name === props.show.previous_show_name);
      if (!previousShow && props.show.previous_show_name) {
        // Try to find by show_type + show_name combination (e.g., "Obedience" + "Trial 1" = "Obedience Trial 1")
        previousShow = allShows.find(s => {
          const combinedName = `${s.show_type} ${s.show_name}`;
          return combinedName === props.show.previous_show_name;
        });
      }
      
      if (previousShow) {
        // Calculate the total duration of the previous show using the same utility
        const previousShowEntries = extractJudgeScheduleFromRings(previousShow, ringName.id);
        const previousShowTotalMinutes = previousShowEntries.reduce((sum: number, entry: any) => sum + entry.totalMinutes, 0);
        
        // Start this show immediately after the previous show ends
        baseStartTime = new Date(previousShow.show_date);
        baseStartTime.setMinutes(baseStartTime.getMinutes() + previousShowTotalMinutes + ringStartOffsetMinutes(previousShow, ringName.id));
      }
    }

    baseStartTime.setMinutes(baseStartTime.getMinutes() + ringStartOffsetMinutes(props.show, ringName.id));

    return baseStartTime;
  }, [props.show, allShows, ringName.id]);

  const formatDisplayedStartTime = useCallback((date: Date, pattern: string) => {
    const tz = props.event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const displayed = show.round_start_times_to_5
      ? ceilToFiveMinuteInterval(date, tz)
      : date;
    return moment.tz(displayed, tz).format(pattern);
  }, [props.event.timezone, show.round_start_times_to_5]);

  // Generate show title with timing information
  const getShowTitleWithTiming = useCallback(() => {
    const showDate = moment(props.show.show_date).format("MMM Do");
    const mainTitle = `${props.show.show_type} ${props.show.show_name} (${showDate})`;
    const startTime = formatDisplayedStartTime(getShowStartTime(), "h:mm A zz");
    
    if (props.show.trailing_show && props.show.previous_show_name) {
      const subTitle = `Follows ${props.show.previous_show_name} (Est. Start time ${startTime})`;
      return { mainTitle, subTitle };
    }
    return { mainTitle, subTitle: `${startTime}` };
  }, [props.show, getShowStartTime, formatDisplayedStartTime]);

  // Update parent component with show title when timing information changes
  useEffect(() => {
    if (onShowTitleChange && allShows.length > 0) {
      const titleData = getShowTitleWithTiming();
      onShowTitleChange(titleData);
    }
  }, [allShows, onShowTitleChange, getShowTitleWithTiming]);

  const getJudgeTiming = (jIndex: number) => {
    const baseStartTime = getShowStartTime();

    // Calculate total minutes from previous judges
    let totalMinutes = 0;
    for (let j = 0; j < jIndex && j < judges.length; j++) {
      // Calculate from dog_classes to include breaks
      const judgeMinutes = (judges[j].dog_classes || [])
        .filter(dc => !dc.group_with) // Exclude grouped children
        .reduce((sum, dc) => {
          if (dc.type === Constants.running_order_dog_class_type.break) {
            // For breaks, duration is the total time
            return sum + (dc.duration || 0);
          }
          // For dog classes, use total_minutes if available, otherwise duration * entries
          const minutes = dc.total_minutes || (dc.duration && dc.entries ? dc.duration * dc.entries : 0);
          return sum + minutes;
        }, 0);
      totalMinutes += judgeMinutes;
    }

    // Add the minutes to the base start time
    const judgeStartTime = new Date(baseStartTime);
    judgeStartTime.setMinutes(judgeStartTime.getMinutes() + totalMinutes);

    return formatDisplayedStartTime(judgeStartTime, "h:mmA zz");
  }

  const getElementTiming = (jIndex: number, sIndex: number) => {
    const baseStartTime = getShowStartTime();

    // Calculate total minutes from previous judges
    let totalMinutes = 0;
    for (let j = 0; j < jIndex && j < judges.length; j++) {
      // Calculate from dog_classes to include breaks
      const judgeMinutes = (judges[j].dog_classes || [])
        .filter(dc => !dc.group_with) // Exclude grouped children
        .reduce((sum, dc) => {
          // For breaks, use duration directly
          if (dc.type === Constants.running_order_dog_class_type.break) {
            return sum + (dc.duration || 0);
          }
          // For dog classes, use total_minutes if available (already accounts for entries), 
          // or calculate as duration * entries (0 entries = 0 time)
          const minutes = dc.total_minutes || (dc.duration && dc.entries ? dc.duration * dc.entries : 0);
          return sum + minutes;
        }, 0);
      totalMinutes += judgeMinutes;
    }

    // Add time from previous classes in current judge
    if (jIndex < judges.length) {
      for (let s = 0; s < sIndex && s < judges[jIndex].dog_classes.length; s++) {
        const dogClass = judges[jIndex].dog_classes[s];
        // Only add time for non-grouped classes and breaks
        if (dogClass.type === 'break' || !dogClass.group_with) {
          // For breaks, use duration directly; for classes, use total_minutes or duration * entries
          const minutesToAdd = dogClass.type === 'break' 
            ? (dogClass.duration || 0)
            : (dogClass.total_minutes || (dogClass.duration && dogClass.entries ? dogClass.duration * dogClass.entries : 0));
          totalMinutes += minutesToAdd;
        }
      }
    }

    // Add the minutes to the base start time
    const classStartTime = new Date(baseStartTime);
    classStartTime.setMinutes(classStartTime.getMinutes() + totalMinutes);

    return formatDisplayedStartTime(classStartTime, "h:mmA zz");
  }

  const breakLengthOptions:IOption[] = [
    { id: "5", value: "5 Minutes" },
    { id: "10", value: "10 Minutes" },
    { id: "15", value: "15 Minutes" },
    { id: "20", value: "20 Minutes" },
    { id: "30", value: "30 Minutes" },
    { id: "45", value: "45 Minutes" },
    { id: "60", value: "60 Minutes" },
    { id: "90", value: "90 Minutes" },
    { id: "120", value: "120 Minutes" }
  ];

  const getBreakValue = (duration: number) => {
    let breakValue:IOption[] = breakLengthOptions.filter((b: IOption) => { return b.id === duration.toString() });

    if (breakValue.length > 0) {
      return breakValue[0];
    }

    return { id: "60", value: "60 Minutes" };
  }

  const getLevelName = (se: IRunningOrderDogClassDrag) => {
    let baseName: string;

    // For ABI Scent Dog Games: new structure stores game name as show_element (no level),
    // old structure stores game name as level. Show as: "<game name> - <section>"
    if (event.sanctioning_club === Constants.sanctioning_club.ABI &&
        propShow.show_type === Constants.show_type.scent_dog_games) {
      const gameName = se.level || se.show_element || '';
      baseName = se.section ? `${gameName} - ${se.section}` : gameName;
      if (se.variation) baseName = `${baseName} (${se.variation})`;
      if (se.sex_split) return `${baseName} - ${se.sex_split}`;
      return baseName;
    }
    
    const isAKCHerding = event.sanctioning_club === Constants.sanctioning_club.AKC && [
      Constants.show_type.herding,
      Constants.show_type.pre_trial_herding,
      Constants.show_type.instinct_test_herding,
    ].includes(props.show.show_type);

    if ([Constants.show_type.scentwork, Constants.show_type.nosework, Constants.show_type.agility, Constants.show_type.scent, Constants.show_type.urban_rat_race, Constants.show_type.cwags_games].includes(props.show.show_type)) {
      baseName = [se.show_element, se.level].join(" - ");
    } else if (isAKCHerding) {
      // Course A/B/C/D repeats the same levels (Started/Intermediate/Advance), so include
      // the course. Pre-Trial/Instinct Test have a single element matching the show type
      // (level holds the stock type directly), so skip the redundant element prefix there.
      baseName = (se.show_element && se.show_element !== props.show.show_type)
        ? [se.show_element, se.level].join(" - ")
        : (se.level || se.show_element || '');
    } else if (isUkcRallyShow(event.sanctioning_club, propShow.show_type)) {
      // "RO1 B" whether stored as level "RO1 B" or as RO1 + section B
      baseName = ukcRallyClassLabel(se.level, se.section) || se.show_element || '';
    } else {
      baseName = se.level || se.show_element || '';
    }
    
    // For C-WAGS Obedience/Rally, AKC Herding, and 4-H Agility with sections, append the
    // stock/section (e.g. Division A/B). UKC Nosework sections always run together — no
    // section label needed in the judge schedule.
    const shouldShowSection = (
      (event.sanctioning_club === Constants.sanctioning_club.C_WAGS && 
       (propShow.show_type === Constants.show_type.obedience || propShow.show_type === Constants.show_type.rally_obedience)) ||
      isAKCHerding ||
      (event.sanctioning_club === Constants.sanctioning_club.FH && propShow.show_type === Constants.show_type.agility)
    );
    
    if (shouldShowSection && se.section) {
      baseName = `${baseName} - ${se.section}`;
    }
    
    // For AKC Specialty shows with variation, append the variation name
    if (se.variation) {
      baseName = `${baseName} (${se.variation})`;
    }
    
    // For AKC Specialty shows, append " - Dogs" or " - Bitches" if sex_split is set
    if (se.sex_split) {
      return `${baseName} - ${se.sex_split}`;
    }
    
    return baseName;
  }

  // ── Split helpers ──────────────────────────────────────────────────────────

  // Returns true if any class tile in the current ring has splits for the same logical class
  const anyClassHasSplits = (allJudges: IRunningOrderDrag[]) => {
    return allJudges.some(j => j.dog_classes.some(dc => (dc as any).split_number !== undefined));
  };

  // Count total dogs assigned across ALL splits of the same logical class
  const getSplitTotalDogs = (se: IRunningOrderDogClassDrag, allJudges: IRunningOrderDrag[]) => {
    let total = 0;
    for (const j of allJudges) {
      for (const dc of j.dog_classes) {
        if (
          dc.show_element === se.show_element &&
          dc.level === se.level &&
          dc.section === se.section &&
          (dc as any).split_number !== undefined
        ) {
          total += (dc.dog_run_order?.length ?? 0);
        }
      }
    }
    return total;
  };

  const splitDogClass = useCallback((jIndex: number, seIndex: number) => {
    const _judges: IRunningOrderDrag[] = JSON.parse(JSON.stringify(judges));
    const tile: IRunningOrderDogClassDrag = _judges[jIndex].dog_classes[seIndex];

    if (!tile || tile.type === Constants.running_order_dog_class_type.break) return;
    if (tile.group_with) {
      toast.show({ title: "Split Class", content: "Ungroup this class before splitting.", duration: 5000, type: "fail" });
      return;
    }

    const currentSplitTotal = tile.split_total ?? 1;
    if (currentSplitTotal >= 4) {
      toast.show({ title: "Split Class", content: "Maximum of 4 splits reached.", duration: 5000, type: "fail" });
      return;
    }

    const newSplitTotal = currentSplitTotal + 1;

    // Section comparison: treat undefined and "" as equivalent
    const sectionEq = (a: any, b: any) => (a || '') === (b || '');

    // Update all existing sibling splits' split_total
    for (const j of _judges) {
      for (const dc of j.dog_classes) {
        const d = dc as any;
        if (
          dc.show_element === tile.show_element &&
          dc.level === tile.level &&
          sectionEq(dc.section, tile.section) &&
          (d.split_number !== undefined || d === tile)
        ) {
          d.split_total = newSplitTotal;
          if (d.split_number === undefined) d.split_number = 1; // promote unsplit tile
        }
      }
    }

    // Capture original values before any mutations so we can compute proportional minutes
    const originalEntries: number = tile.entries ?? 0;
    const originalMins: number | undefined = tile.total_minutes || undefined;
    // Per-entry duration: prefer the explicit `duration` field, otherwise derive from totals
    const durationPerEntry: number | undefined =
      (tile as any).duration ??
      (originalMins && originalEntries ? originalMins / originalEntries : undefined);

    // Compute total_minutes proportionally for a given entry count
    const minsForCount = (count: number): number | undefined => {
      if (!count) return 0;
      if (durationPerEntry) return Math.round(count * durationPerEntry * 10) / 10;
      if (originalMins && originalEntries)
        return Math.round((originalMins * count / originalEntries) * 10) / 10;
      return undefined;
    };

    // Distribute dog_run_order alternately if populated
    const existingOrder = tile.dog_run_order ?? [];
    if (existingOrder.length > 0) {
      const split1Dogs: string[] = [];
      const split2Dogs: string[] = [];
      existingOrder.forEach((id, i) => (i % 2 === 0 ? split1Dogs : split2Dogs).push(id));
      const split1Count = split1Dogs.length || Math.ceil(originalEntries / 2);
      const split2Count = split2Dogs.length || Math.floor(originalEntries / 2);
      tile.dog_run_order = split1Dogs;
      tile.entries = split1Count;
      tile.total_minutes = minsForCount(split1Count) ?? tile.total_minutes;
      const newTile: IRunningOrderDogClassDrag = {
        ...JSON.parse(JSON.stringify(tile)),
        split_number: newSplitTotal,
        dog_run_order: split2Dogs,
        entries: split2Count,
        total_minutes: minsForCount(split2Count) ?? (originalMins ? Math.ceil(originalMins / 2) : originalMins),
        index: 0,
      };
      _judges[jIndex].dog_classes.splice(seIndex + 1, 0, newTile);
    } else {
      // dog_run_order not yet populated — distribute entries count only (dogs shown via Run Order)
      const split1Entries = Math.ceil(originalEntries / 2);
      const split2Entries = Math.floor(originalEntries / 2);
      tile.entries = split1Entries;
      tile.total_minutes = minsForCount(split1Entries) ?? tile.total_minutes;
      const newTile: IRunningOrderDogClassDrag = {
        ...JSON.parse(JSON.stringify(tile)),
        split_number: newSplitTotal,
        dog_run_order: [],
        entries: split2Entries,
        total_minutes: minsForCount(split2Entries) ?? tile.total_minutes,
        index: 0,
      };
      _judges[jIndex].dog_classes.splice(seIndex + 1, 0, newTile);
    }

    const updated = resetDraggableIndexes(_judges);
    runningOrderLoadVersionRef.current++;
    setJudges(updated);
    debouncedSave.cancel();
    saveJudgeScheduleRef.current(undefined, updated, true);
  }, [judges, debouncedSave, saveJudgeScheduleRef, runningOrderLoadVersionRef]);

  const mergeSplits = useCallback((jIndex: number, seIndex: number) => {
    const _judges: IRunningOrderDrag[] = JSON.parse(JSON.stringify(judges));
    const tile: IRunningOrderDogClassDrag = _judges[jIndex].dog_classes[seIndex];

    if (!tile || (tile as any).split_number === undefined) return;

    // Section comparison: treat undefined and "" as equivalent
    const sectionEq = (a: any, b: any) => (a || '') === (b || '');

    const isSibling = (dc: any) =>
      dc.show_element === tile.show_element &&
      dc.level === tile.level &&
      sectionEq(dc.section, tile.section) &&
      dc.split_number !== undefined;

    // Collect ALL sibling split tiles sorted by split_number
    const allSplits: Array<{ j: IRunningOrderDrag; i: number }> = [];
    for (const j of _judges) {
      for (let i = 0; i < j.dog_classes.length; i++) {
        if (isSibling(j.dog_classes[i])) allSplits.push({ j, i });
      }
    }
    allSplits.sort((a, b) => (a.j.dog_classes[a.i] as any).split_number - (b.j.dog_classes[b.i] as any).split_number);

    if (allSplits.length < 2) return;

    // Pool all dogs from every split into split 1 (the anchor tile)
    const { j: anchorJ, i: anchorI } = allSplits[0];
    const anchorTile = anchorJ.dog_classes[anchorI] as any;

    const pooledDogs: string[] = [...(anchorTile.dog_run_order ?? [])];
    let totalEntries = anchorTile.entries ?? 0;
    let totalMinutes = anchorTile.total_minutes ?? 0;

    for (let s = 1; s < allSplits.length; s++) {
      const { j, i } = allSplits[s];
      const sib = j.dog_classes[i] as any;
      pooledDogs.push(...(sib.dog_run_order ?? []));
      totalEntries += sib.entries ?? 0;
      totalMinutes += sib.total_minutes ?? 0;
    }

    anchorTile.dog_run_order = pooledDogs;
    anchorTile.entries = totalEntries;
    anchorTile.total_minutes = totalMinutes;

    // Remove all non-anchor split tiles (iterate backwards by index to keep indices valid)
    // Group removals per judge in reverse index order
    const removalMap = new Map<IRunningOrderDrag, number[]>();
    for (let s = 1; s < allSplits.length; s++) {
      const { j, i } = allSplits[s];
      if (!removalMap.has(j)) removalMap.set(j, []);
      removalMap.get(j)!.push(i);
    }
    removalMap.forEach((indices, j) => {
      indices.sort((a, b) => b - a); // descending so earlier splices don't shift later ones
      for (const idx of indices) {
        j.dog_classes.splice(idx, 1);
      }
    });

    // Clear split fields on the anchor tile (now the only tile)
    delete anchorTile.split_number;
    delete anchorTile.split_total;

    const updated = resetDraggableIndexes(_judges);
    runningOrderLoadVersionRef.current++;
    setJudges(updated);
    debouncedSave.cancel();
    saveJudgeScheduleRef.current(undefined, updated, true);
    setMergingDogClass(null);
  }, [judges, debouncedSave, saveJudgeScheduleRef, runningOrderLoadVersionRef]);

  // ── End Split helpers ──────────────────────────────────────────────────────

  const getLevelContent = (jIndex: number, seIndex: number, se: IRunningOrderDogClassDrag, judge?: IRunningOrderDrag) => {
    const levelKey = `${se.show_element}-${se.level}`;
    const selectionKey = `${jIndex}-${seIndex}-${levelKey}`;
    const isSelected = selectedForGrouping.includes(selectionKey);
    const isGrouped = isLevelGrouped(se);
    const groupedLevels = getGroupedLevels(se);

    if (isUKCConformationBISTile(se)) {
      return (<>
        <div className={`${styles.ElementIconWrap} ${styles.Break}`}>
          <div className={`${styles.icon} ${styles.break}`}></div>
        </div>
        <div className={`${styles.ElementRight} ${styles.Break}`}>
          <div className={styles.ElementName}>{se.bis_class}</div>
          <div className={styles.EntryAmount}>
            <Dropdown className={styles.BreakLength} value={getBreakValue(se.duration || Constants.ukc_conformation_bis_default_minutes)} label={"Duration"} onChange={(e, value: IOption) => { changeBreakLength(jIndex, seIndex, Number(value.id)); }} options={breakLengthOptions} placeholder="" />
          </div>
        </div>
      </>);
    } else if (se.type === Constants.running_order_dog_class_type.break) {
      return (<>
        <div className={`${styles.ElementIconWrap} ${styles.Break}`}>
          <div className={`${styles.icon} ${styles.break}`}></div>
        </div>
        <div className={`${styles.ElementRight} ${styles.Break}`}>
          <div className={styles.ElementName}>
            <input 
              type="text"
              value={(se as any).name || ""}
              onChange={(e) => updateBreakNameLocal(jIndex, seIndex, e.target.value)}
              onBlur={(e) => {
                // If empty when saving, default to "Break"
                if (!e.target.value.trim()) {
                  updateBreakNameLocal(jIndex, seIndex, "Break");
                }
                saveBreakName(jIndex, seIndex);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              className={styles.BreakNameInput}
              placeholder="Break name"
            />
            <span onClick={() => { removeBreak(jIndex, seIndex); }}></span>
          </div>
          <div className={styles.EntryAmount}>
            <Dropdown className={styles.BreakLength} value={getBreakValue(se.duration || 60)} label={"Break Length"} onChange={(e, value: IOption) => { changeBreakLength(jIndex, seIndex, Number(value.id)); }} options={breakLengthOptions} placeholder="" />
          </div>
        </div>
      </>);
    } else {
      return(<>
        <div className={styles.ElementIconWrap}>
          {!groupingMode && <div className={`${styles.icon} ${styles.handle}`}></div>}
          {groupingMode && (
            <div className={`${styles.groupCheckbox} ${isSelected ? styles.selected : ''}`}>
              <div className={styles.checkmark}>✓</div>
            </div>
          )}
          {isGrouped && !groupingMode && (
            <div className={styles.groupIndicator} title="This level is grouped with others">
              🔗
            </div>
          )}
        </div>
        <div className={styles.ElementRight}>
          <div className={styles.ElementName}>
            <span>{getLevelName(se)}</span>
            {(se as any).split_number !== undefined && (
              <span
                className={styles.splitBadge}
                title={`Split ${(se as any).split_number} of ${(se as any).split_total} — drag independently or use Merge to recombine`}
              >
                {(se as any).split_number}/{(se as any).split_total}
              </span>
            )}
            {(isGrouped || groupedLevels.length > 0) && !groupingMode && (
              <div className={styles.groupedLevels}>
                <span className={styles.groupedWith}>
                  {groupedLevels.length > 0 
                    ? `Grouped with: ${groupedLevels.join(', ')}` 
                    : se.group_with 
                      ? `Grouped with: ${se.group_with}`
                      : 'This level is grouped'}
                </span>
                <Button 
                  variant="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    ungroupLevel(se);
                  }}
                  title="Ungroup this level"
                >
                  ungroup
                </Button>
              </div>
            )}
          </div>
          <div className={styles.EntryAmount}>
            <div>
              {getElementCountsDisplay(se, judge?.judge_name)}
              {(se as any).split_number !== undefined && !(se.dog_run_order?.length) && (
                <span className={styles.splitUnassigned}> — dogs unassigned</span>
              )}
              {/* Show minutes per entry with 1 decimal place */}
              {se.total_minutes ? ` (${(se.total_minutes).toFixed(1)} mins)` : null}
            </div>
            <div className={styles.startTime}>Est. start time: {getElementTiming(jIndex, seIndex)}</div>
            {(se.duration !== undefined && se.duration !== null) && <div className={styles.startTime}>Est. time per entry: {se.duration.toFixed(2)} minute(s)</div>}
            {!!se.time_limit && !se.time_limit_search_1 && <div className={styles.startTime}>Time limit: {se.time_limit}</div>}
            {!!se.num_hides && !se.num_hides_search_1 && <div className={styles.startTime}>Number of hides: {se.num_hides}</div>}
            {/* Master level - Search 1 */}
            {!!se.time_limit_search_1 && <div className={styles.startTime}>Time limit (Search 1): {se.time_limit_search_1}</div>}
            {!!se.num_hides_search_1 && <div className={styles.startTime}>Number of hides (Search 1): {se.num_hides_search_1}</div>}
            {/* Master level - Search 2 */}
            {!!se.time_limit_search_2 && <div className={styles.startTime}>Time limit (Search 2): {se.time_limit_search_2}</div>}
            {!!se.num_hides_search_2 && <div className={styles.startTime}>Number of hides (Search 2): {se.num_hides_search_2}</div>}
            {/* Master Interior - Search 3 */}
            {!!se.time_limit_search_3 && <div className={styles.startTime}>Time limit (Search 3): {se.time_limit_search_3}</div>}
            {!!se.num_hides_search_3 && <div className={styles.startTime}>Number of hides (Search 3): {se.num_hides_search_3}</div>}
          </div>
        </div>
      </>);
    }
  }

  const addRing = async () => {
    // Find the next available ring number from current state
    const existingRingNumbers = (showRef.current?.rings ?? []).map((ring: any) => {
      const match = ring.ring.match(/Ring (\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    });
    const nextRingNumber = existingRingNumbers.length > 0 ? Math.max(...existingRingNumbers) + 1 : 2;
    const newRingName: string = `Ring ${nextRingNumber}`;

    // Same hand-off as handleRingChange: persist the ring we're leaving, then clear
    // `judges` so nothing treats its judges as the new ring's.
    commitJudgesToShow();
    debouncedSave.flush();

    try {
      // Use targeted endpoint — fetches fresh from DB and only appends the new ring.
      // No stale show state is involved, so Ring 1's running order is never overwritten.
      const response = await apiService.addRingToShow(props.event._id, props.show._id, newRingName);
      judgesRingIdRef.current = null;
      setJudges([]);
      setRingName({ id: newRingName, value: newRingName });
      setRingOptions([...ringOptions, { id: newRingName, value: newRingName }]);
      setShow(response);
      setLoaded(false);
      onUpdate(response);
    } catch (error: any) {
      console.error('Error adding ring:', error);
      toast.show({
        title: "Judge Schedules",
        content: error.response?.data?.message ?? "Error adding new ring, please try again",
        duration: 10000,
        type: "fail"
      });
    }
  }

  const deleteCurrentRing = () => {
    let hasRing:IShowRing[] = show.rings?.filter((r: IShowRing, index: number) => { return r.ring === ringName.id; }) ?? [];

    if (hasRing.length > 0) {
      if (hasRing[0].running_order && hasRing[0].running_order.length === 0) {
        const ringToDelete = hasRing[0].ring;
        const remainingRings = show.rings?.filter((r: IShowRing) => r.ring !== ringToDelete) ?? [];
        const prevRing = remainingRings[remainingRings.length - 1];

        apiService.deleteRingFromShow(props.event._id, props.show._id, ringToDelete).then((response) => {
          debouncedSave.cancel();
          judgesRingIdRef.current = null;
          setJudges([]);
          setRingName({ id: prevRing?.ring ?? "Ring 1", value: prevRing?.ring ?? "Ring 1" });
          setLoaded(false);
          setRingOptions(remainingRings.map((r: IShowRing) => ({ id: r.ring, value: r.ring })));
          setShow(response);
          onUpdate(response);
        }).catch((error: any) => {
          toast.show({
            title: "Judge Schedules",
            content: error.response?.data?.message ?? "Error removing ring, please try again",
            duration: 10000,
            type: "fail"
          });
        });
      } else {
        toast.show({
          title: "Judge Schedules",
          content: "Can't remove "+ ringName.id +", the ring still has judges in it",
          duration: 10000,
          type: "fail"
        });
      }
    }
  }

  const moveJudges = async (movedJudges: ISelectedJudge[], newRing?: string) => {
    // Cancel any pending debounced save and wait for an already-in-flight save to
    // complete before fetching a fresh copy of the show. This ensures the DB is
    // current before we modify rings.
    debouncedSave.cancel();
    const saveKey = `${event._id}-${propShow._id}`;
    const pendingSave = pendingShowSaves.get(saveKey);
    if (pendingSave) await pendingSave;

    // Fetch fresh show from DB to avoid using stale React state which may be
    // missing dog_run_order arrays updated by the RunOrderTab.
    let _show: any;
    try {
      const allShows = await apiService.getClubEventShows(event.club_id, event._id);
      _show = allShows.find((s: any) => s._id === propShow._id);
      if (!_show) _show = JSON.parse(JSON.stringify(showRef.current));
    } catch {
      _show = JSON.parse(JSON.stringify(showRef.current));
    }

    // Track which ring names were actually modified so we know which rings to save.
    const modifiedRingNames = new Set<string>();

    movedJudges.forEach(movedJudge => {
      const targetRing = _show.rings.find((ring: any) => newRing ? ring.ring === newRing : ring.ring === ringName.id);

      if (targetRing) {
        // Search fresh DB data first; fall back to in-memory showRef if the
        // running_order hasn't been persisted yet (e.g. unsaved local edits).
        // When moving out of the ring on screen, take the judge from that ring — the same
        // judge can also appear in another ring. Never pick the target ring as the source.
        const ringHasJudge = (ring: any) =>
          Array.isArray(ring.running_order) &&
          ring.running_order.some((ro: any) => ro.judge_name === movedJudge.judge_name);
        let oldRing = (targetRing.ring !== ringName.id
          ? _show.rings.find((ring: any) => ring.ring === ringName.id && ringHasJudge(ring))
          : undefined) ??
          _show.rings.find((ring: any) => ring.ring !== targetRing.ring && ringHasJudge(ring));

        if (!oldRing) {
          const memRing = (showRef.current.rings || []).find((ring: any) =>
            ring.ring !== targetRing.ring &&
            Array.isArray(ring.running_order) &&
            ring.running_order.some((ro: any) => ro.judge_name === movedJudge.judge_name)
          );
          if (memRing) {
            oldRing = _show.rings.find((ring: any) => ring.ring === memRing.ring);
            if (oldRing && !Array.isArray(oldRing.running_order)) {
              oldRing.running_order = JSON.parse(JSON.stringify(memRing.running_order));
            }
          }
        }

        // Fallback: the judge may exist only in the UI's built state (e.g. UKC Conformation
        // judges built from breed_group_judges but not yet saved to DB). In that case,
        // reconstruct the ring's running_order from judgesRef.current so the move can proceed.
        if (!oldRing) {
          const isInCurrentRingUI = judgesRef.current.some(
            (j) => j.judge_name === movedJudge.judge_name &&
              (movedJudge.judge_number === "" || (j.judge_number || "") === movedJudge.judge_number)
          );
          if (isInCurrentRingUI) {
            oldRing = _show.rings.find((ring: any) => ring.ring === ringName.id);
            if (oldRing) {
              // Populate the ring's running_order from the current UI state so
              // the save writes the full built schedule (not just the single judge).
              oldRing.running_order = judgesRef.current.map((j) => ({
                judge_name: j.judge_name,
                judge_number: j.judge_number || "",
                dog_classes: (j.dog_classes || []).map((dc: any) => ({
                  type: dc.type,
                  show_element: dc.show_element,
                  level: dc.level,
                  section: dc.section,
                  entries: dc.entries,
                  total_minutes: dc.total_minutes,
                  duration: dc.duration,
                  dog_run_order: dc.dog_run_order,
                  completed_dogs: dc.completed_dogs,
                  sex_split: dc.sex_split,
                  variation: dc.variation,
                  group_with: dc.group_with,
                  group_order: dc.group_order,
                  label: dc.label,
                  name: dc.name,
                  split_number: dc.split_number,
                  split_total: dc.split_total,
                  start_letter: dc.start_letter,
                  end_letter: dc.end_letter,
                })),
              }));
            }
          }
        }

        if (!oldRing) return;

        // Special case: "Multiple Judges" aggregate entry (e.g. AKC Conformation).
        // The running_order stores all judges as one entry; the UI expands them into
        // individual rows. Only move the specific judge's dog_classes, not the whole entry.
        const isAggregate = movedJudge.judge_name === "Multiple Judges" && movingJudge !== -1;
        if (isAggregate) {
          const actualJudge = judgesRef.current[movingJudge];
          if (actualJudge) {
            const classKey = (dc: any) =>
              `${dc.show_element}|${dc.level}|${dc.sex_split ?? ""}|${dc.split_number ?? ""}`;

            const movedClassKeys = new Set(
              (actualJudge.dog_classes || [])
                .filter((dc: any) => dc.type !== Constants.running_order_dog_class_type.break)
                .map(classKey)
            );

            // Remove those classes from the source "Multiple Judges" entry
            const sourceEntry = oldRing.running_order.find((ro: any) => ro.judge_name === "Multiple Judges");
            if (sourceEntry) {
              sourceEntry.dog_classes = (sourceEntry.dog_classes || []).filter(
                (dc: any) => !movedClassKeys.has(classKey(dc))
              );
              if (sourceEntry.dog_classes.length === 0) {
                const idx = oldRing.running_order.indexOf(sourceEntry);
                if (idx !== -1) oldRing.running_order.splice(idx, 1);
              }
            }

            // Add as individual entry to target ring
            if (!Array.isArray(targetRing.running_order)) targetRing.running_order = [];
            const existingEntry = targetRing.running_order.find(
              (ro: any) => ro.judge_name === actualJudge.judge_name
            );
            if (existingEntry) {
              existingEntry.dog_classes = [
                ...(existingEntry.dog_classes || []),
                ...(actualJudge.dog_classes || []),
              ];
            } else {
              targetRing.running_order.push({
                judge_name: actualJudge.judge_name,
                judge_number: actualJudge.judge_number || "",
                dog_classes: [...(actualJudge.dog_classes || [])],
              });
            }
            modifiedRingNames.add(oldRing.ring);
            modifiedRingNames.add(targetRing.ring);
            return;
          }
        }

        const judgeIndex = oldRing.running_order!.findIndex(
          (judge: any) => judge.judge_name === movedJudge.judge_name
        );

        if (judgeIndex !== -1 && oldRing !== targetRing) {
          const judge = oldRing.running_order!.splice(judgeIndex, 1)[0];
          if (!Array.isArray(targetRing.running_order)) targetRing.running_order = [];
          const existingEntry = targetRing.running_order.find((ro: any) =>
            ro.judge_name === judge.judge_name && (ro.judge_number || "") === (judge.judge_number || "")
          );
          if (existingEntry) {
            // Judge is already in the target ring: merge rather than add a second row,
            // skipping classes the target row already has.
            const classKey = (dc: any) => [
              dc.type, dc.show_element, dc.level, dc.section, dc.sex_split, dc.variation,
              dc.split_number, dc.start_letter, dc.end_letter,
            ].map((v) => v ?? "").join("|");
            const targetKeys = new Set((existingEntry.dog_classes || []).map(classKey));
            existingEntry.dog_classes = [
              ...(existingEntry.dog_classes || []),
              ...(judge.dog_classes || []).filter((dc: any) =>
                dc.type === Constants.running_order_dog_class_type.break || !targetKeys.has(classKey(dc))
              ),
            ];
          } else {
            targetRing.running_order.push(judge);
          }
          modifiedRingNames.add(oldRing.ring);
          modifiedRingNames.add(targetRing.ring);
        }
      }
    });

    if (modifiedRingNames.size === 0) {
      // Nothing actually moved — close modal and bail.
      setMovingJudge(-1);
      toggle();
      return;
    }

    // Update showRef synchronously so any stale debounced save that fires later
    // uses the correct post-move state as its merge base.
    showRef.current = _show;

    // Save each modified ring individually using the targeted ring endpoint.
    // This avoids the full-show write-conflict and its retry (which would use
    // stale judgesRef.current and re-add the moved judge to the source ring).
    try {
      for (const ringNameToSave of modifiedRingNames) {
        const ringToSave = _show.rings?.find((r: any) => r.ring === ringNameToSave);
        if (ringToSave) {
          const apiCall = apiService.updateRingRunningOrder(
            event._id,
            propShow._id,
            ringNameToSave,
            ringToSave.running_order || []
          );
          pendingShowSaves.set(saveKey, apiCall.catch(() => {}));
          await apiCall;
          pendingShowSaves.delete(saveKey);
        }
      }
    } catch (error: any) {
      pendingShowSaves.delete(saveKey);
      toast.show({
        title: "Move Judge",
        content: error?.response?.data?.message || "Something went wrong, please try again",
        duration: 6000,
        type: "fail",
      });
      setMovingJudge(-1);
      toggle();
      return;
    }

    // Cancel any debounce that might have been queued during the async operations above.
    // This prevents a stale judgesRef.current (still containing the moved judge) from
    // being saved over the freshly-written ring data before the reload updates judges.
    debouncedSave.cancel();

    setShow(_show);
    onUpdate(_show);
    setMovingJudge(-1);
    toggle();
    // Trigger a fresh reload so the UI reflects the moved judge immediately.
    setLoaded(false);
  }


  const handleMoveClassToRing = async (targetRingName: string, targetJudgeName: string, targetJudgeNumber: string) => {
    if (!movingDogClass || !movingDogClassSourceJudge) return;

    // Flush any queued debounced save FIRST so it doesn't race with the cross-ring saves.
    // A debounce from a previous drag may still be pending in the timeout queue even
    // though the modal is open; flushing it here ensures the DB is up-to-date and
    // prevents it from firing concurrently and overwriting our changes.
    debouncedSave.flush();

    // Await any in-flight ring save so DB is current before we read the target ring
    const saveKey = `${event._id}-${propShow._id}`;
    const pendingSave = pendingShowSaves.get(saveKey);
    if (pendingSave) await pendingSave;

    // Fetch fresh show from DB to get the current target ring's running order
    let latestShow: any;
    try {
      const allShows = await apiService.getClubEventShows(event.club_id, event._id);
      latestShow = allShows.find((s: any) => s._id === propShow._id);
    } catch {
      latestShow = showRef.current;
    }

    // --- Build new source ring running order from in-memory judges state ---
    // judgesRef.current is always the most up-to-date state for the current ring.
    if (judgesRingIdRef.current !== ringName.id) {
      toast.show({ title: "Move Class", content: "This ring is still loading, please try again.", duration: 5000, type: "fail" });
      return;
    }
    const sourceRunningOrder: any[] = JSON.parse(JSON.stringify(judgesRef.current));
    const sourceJudge = sourceRunningOrder.find((ro: any) => ro.judge_name === movingDogClassSourceJudge);

    if (!sourceJudge) {
      toast.show({ title: "Move Class", content: "Source judge not found.", duration: 5000, type: "fail" });
      return;
    }

    const classIndex = sourceJudge.dog_classes?.findIndex((dc: any) =>
      dc.show_element === movingDogClass.show_element &&
      dc.level === movingDogClass.level &&
      dc.section === movingDogClass.section &&
      dc.sex_split === movingDogClass.sex_split &&
      dc.variation === movingDogClass.variation &&
      dc.split_number === (movingDogClass as any).split_number
    );

    if (classIndex === undefined || classIndex === -1) {
      toast.show({ title: "Move Class", content: "Class not found.", duration: 5000, type: "fail" });
      return;
    }

    const [removedClass] = sourceJudge.dog_classes.splice(classIndex, 1);

    // --- Build new target ring running order from fresh DB data ---
    const targetRingData = latestShow?.rings?.find((r: any) => r.ring === targetRingName);
    const targetRunningOrder: any[] = JSON.parse(JSON.stringify(targetRingData?.running_order ?? []));
    const targetJudge = targetRunningOrder.find(
      (ro: any) => ro.judge_name === targetJudgeName && (ro.judge_number || "") === targetJudgeNumber
    );

    if (!targetJudge) {
      toast.show({ title: "Move Class", content: "Target judge not found.", duration: 5000, type: "fail" });
      return;
    }

    // Drop any copy of this exact tile already in the target ring so the move can't
    // leave the class scheduled twice.
    const tileKey = (dc: any) => [
      dc.show_element, dc.level, dc.section, dc.sex_split, dc.variation,
      dc.split_number, dc.start_letter, dc.end_letter,
    ].map((v) => v ?? "").join("|");
    const movedKey = tileKey(removedClass);
    targetRunningOrder.forEach((ro: any) => {
      ro.dog_classes = (ro.dog_classes || []).filter((dc: any) =>
        dc.type === Constants.running_order_dog_class_type.break || tileKey(dc) !== movedKey
      );
    });
    if (!targetJudge.dog_classes) targetJudge.dog_classes = [];
    targetJudge.dog_classes.push(removedClass);

    setMovingDogClass(null);
    setMovingDogClassSourceJudge("");
    toggle();

    try {
      // Save source ring first, then target ring sequentially so they don't conflict.
      // Each updateRingRunningOrder fetches fresh from DB and only patches its own ring.
      // Register in pendingShowSaves so any concurrent operation can await completion.
      const saveKey = `${event._id}-${propShow._id}`;
      const crossRingSave = (async () => {
        await apiService.updateRingRunningOrder(event._id, propShow._id, ringName.id, sourceRunningOrder);
        return apiService.updateRingRunningOrder(event._id, propShow._id, targetRingName, targetRunningOrder);
      })();
      pendingShowSaves.set(saveKey, crossRingSave.catch(() => {}));
      const finalResponse = await crossRingSave;
      pendingShowSaves.delete(saveKey);

      setShow(finalResponse);
      onUpdate(finalResponse);
      setLoaded(false);

      // Update score records to point to the new judge after cross-ring class move.
      // Same pattern as same-ring drag (bulkUpdateScoreJudge calls in the drag handler).
      const scoreIds = removedClass.dog_run_order || [];
      const isSplitTile = (removedClass as any).split_number !== undefined;
      if (scoreIds.length > 0) {
        apiService.bulkUpdateScoreJudge(
          event._id,
          { _id: { $in: scoreIds } },
          { judge_name: targetJudgeName, judge_number: targetJudgeNumber }
        ).catch((error) => {
          console.error('Failed to update score judge assignments after cross-ring class move:', error);
        });
      } else if (!isSplitTile && (removedClass.level || removedClass.show_element)) {
        // Fallback: no dog_run_order yet (class not yet synced); query by show + element + level + source judge
        const scoreFilter: any = {
          show_id: propShow._id,
          judge_name: movingDogClassSourceJudge,
        };
        if (removedClass.show_element) scoreFilter['dog_class.show_element'] = removedClass.show_element;
        if (removedClass.level) scoreFilter['dog_class.level'] = removedClass.level;
        if ((removedClass as any).section) scoreFilter['dog_class.section'] = (removedClass as any).section;
        apiService.bulkUpdateScoreJudge(
          event._id,
          scoreFilter,
          { judge_name: targetJudgeName, judge_number: targetJudgeNumber }
        ).catch((error) => {
          console.error('Failed to update score judge assignments (fallback) after cross-ring class move:', error);
        });
      }

      // Keep show_elements in sync so NEW registrations for this class default to the
      // target judge too (see same-ring drag handler for full rationale).
      if (!isSplitTile && (removedClass.show_element || removedClass.level)) {
        apiService.updateShowElementJudge(
          event._id,
          propShow._id,
          {
            show_element: removedClass.show_element || '',
            level: removedClass.level,
            section: (removedClass as any).section,
            judge_name: targetJudgeName,
            judge_number: targetJudgeNumber,
          }
        ).catch((error) => {
          console.error('Failed to update show_elements judge assignment after cross-ring class move:', error);
        });
      }

      const classLabel = [removedClass.show_element, removedClass.level]
        .filter(Boolean)
        .join(" - ");
      const entryCount = removedClass.entries ?? (removedClass.dog_run_order?.length ?? 0);
      const entryLabel = `${entryCount} ${entryCount === 1 ? "entry" : "entries"}`;
      toast.show({
        title: "Move Class",
        content: `Moved ${classLabel} (${entryLabel}) to ${targetJudgeName} in ${targetRingName}.`,
        duration: 3000,
        type: "success",
      });
    } catch (error: any) {
      const saveKey = `${event._id}-${propShow._id}`;
      pendingShowSaves.delete(saveKey);
      toast.show({
        title: "Move Class",
        content: error.response?.data?.message || "Failed to save. Please try again.",
        duration: 10000,
        type: "fail",
      });
    }
  };

  const handleDogClassUpdate = (values: IEditRunningOrderDogClassValues) => {
    if (selectedDogClass && selectedJudgeName) {
      const { duration, timeLimit, numHides, searches } = values;

      let payload: RunningOrderDogClassDto = {
        type: selectedDogClass.type,
        duration,
        show_element: selectedDogClass.show_element,
        level: selectedDogClass.level,
      };

      if (!!timeLimit) {
        payload.time_limit = timeLimit;
      }
      if (!!numHides) {
        payload.num_hides = numHides;
      }

      // Multi-search classes (Master, Excellent Interior): per-search fields
      const [search1, search2, search3] = searches ?? [];
      if (!!search1?.timeLimit) payload.time_limit_search_1 = search1.timeLimit;
      if (!!search1?.numHides) payload.num_hides_search_1 = search1.numHides;
      if (!!search2?.timeLimit) payload.time_limit_search_2 = search2.timeLimit;
      if (!!search2?.numHides) payload.num_hides_search_2 = search2.numHides;
      if (!!search3?.timeLimit) payload.time_limit_search_3 = search3.timeLimit;
      if (!!search3?.numHides) payload.num_hides_search_3 = search3.numHides;

      apiService.updateShowRunningOrderDogClass(
        props.event._id,
        props.show._id,
        ringName.id,
        selectedJudgeName,
        payload,
      )
        .then(() => {
          toggle();

          setJudges(prev =>
            prev.map(ro => {
              const dogClass = ro.dog_classes.find((dc) =>
                dc.show_element === selectedDogClass.show_element && 
                dc.level === selectedDogClass.level &&
                dc.sex_split === selectedDogClass.sex_split &&
                dc.variation === selectedDogClass.variation
              )
              if (dogClass) {
                dogClass.duration = duration;
                if (dogClass.entries) dogClass.total_minutes = duration * dogClass.entries;
                if (payload.time_limit) dogClass.time_limit = payload.time_limit;
                if (payload.num_hides) dogClass.num_hides = payload.num_hides;
                if (payload.time_limit_search_1) dogClass.time_limit_search_1 = payload.time_limit_search_1;
                if (payload.num_hides_search_1) dogClass.num_hides_search_1 = payload.num_hides_search_1;
                if (payload.time_limit_search_2) dogClass.time_limit_search_2 = payload.time_limit_search_2;
                if (payload.num_hides_search_2) dogClass.num_hides_search_2 = payload.num_hides_search_2;
                if (payload.time_limit_search_3) dogClass.time_limit_search_3 = payload.time_limit_search_3;
                if (payload.num_hides_search_3) dogClass.num_hides_search_3 = payload.num_hides_search_3;
              }
              return ro;
            })
          );
          
          // Note: Run Order notification happens in debouncedSave to avoid duplicate calls
        })
        .catch(() => {
          toast.show({
            title: "Edit Dog Class",
            content: "Couldn't update the dog class. Please try again later",
            duration: 10000,
            type: "fail",
          });
        });
    }
  }

  const handleAddRunningOrderJudge = async (judgeName: string, judgeNumber: string) => {
    // Flush any queued debounced save first so a stale drag snapshot doesn't
    // overwrite the ring after we add the new judge.
    debouncedSave.flush();

    // Await any in-flight ring saves so DB is fully current before addRingRunningOrder reads it.
    const saveKey = `${event._id}-${propShow._id}`;
    const pendingSave = pendingShowSaves.get(saveKey);
    if (pendingSave) await pendingSave;

    try {
      const updatedShow = await apiService.addRingRunningOrder(
        props.event._id,
        props.show._id,
        ringName.id,
        {
          judge_name: judgeName,
          judge_number: judgeNumber,
          dog_classes: [],
        }
      );

      toggle();

      if (updatedShow) {
        setShow(updatedShow);
        onUpdate(updatedShow);
      }

      // Reload so the new judge's classes get populated from the running order summary.
      // We intentionally skip syncShowRingsWithScores here — it reads and saves ALL rings
      // which would overwrite concurrent ring changes. The Reset button does a full sync
      // when the user explicitly wants it.
      setLoaded(false);
    } catch (error) {
      toast.show({
        title: "Add Judge",
        content: "Couldn't add the judge. Please try again later",
        duration: 10000,
        type: "fail",
      });
    }
  }

  const handleEditRunningOrderJudge = (judgeName: string, judgeNumber: string) => {
    if (!editingJudge) {
      return;
    }

    apiService
      .editRunningOrderJudge(
        props.event._id,
        props.show._id,
        ringName.id,
        {
          old_judge_name: editingJudge.judge_name,
          old_judge_number: editingJudge.judge_number || "",
          judge_name: judgeName,
          judge_number: judgeNumber,
        }
      )
      .then((updatedShow: any) => {
        setEditingJudge(null);
        toggle();

        if (updatedShow) {
          setShow(updatedShow);
          onUpdate(updatedShow);
        }
        setLoaded(false);

        toast.show({
          title: "Edit Judge",
          content: "Judge updated successfully.",
          duration: 3000,
          type: "success",
        });
      })
      .catch((error) => {
        toast.show({
          title: "Edit Judge",
          content: error?.response?.data?.message || "Couldn't update judge. Please try again later.",
          duration: 10000,
          type: "fail",
        });
      });
  };

  const getModalContent = () => {
    switch (modalContent) {
      case "selectJudgeModal":
        return <SelectJudges
            show={props.show}
            onFinish={moveJudges}
          />;
      case "selectRingModal":
        return <SelectRing
          currentRing={ringName.id}
          ringOptions={ringOptions}
          onFinish={(newRing: string) => {
            if (movingJudge !== -1) {
              // Always derive the judge identity from the UI's judges array (judgesRef.current).
              // Using ro[movingJudge] is unreliable because the UI index and the running_order
              // index can diverge (e.g. when the running_order contains a "Multiple Judges"
              // aggregate while the UI shows individual rows expanded from breed_group_judges).
              const uiJudge = judgesRef.current[movingJudge];
              if (uiJudge) {
                moveJudges([{ judge_name: uiJudge.judge_name, judge_number: uiJudge.judge_number || "" }], newRing);
                // moveJudges owns the modal lifecycle (calls toggle internally).
                return;
              }
            }
            // No judge was being moved — just close the modal.
            toggle();
          }}
        />;
      case "editRODogClass":
        return <EditRunningOrderDogClass
          event={event}
          show={show}
          dogClass={selectedDogClass}
          onSubmit={handleDogClassUpdate}
        />
      case "addRunningOrderJudge":
        return <AddRunningOrderJudge
          event={event}
          onSubmit={handleAddRunningOrderJudge}
        />
      case "editRunningOrderJudge":
        return <AddRunningOrderJudge
          event={event}
          title="Edit Judge"
          submitLabel="Update"
          initialName={editingJudge?.judge_name || ""}
          initialNumber={editingJudge?.judge_number || ""}
          onSubmit={handleEditRunningOrderJudge}
        />
      case "moveClassToRing":
        return <MoveClassToRing
          currentRing={ringName.id}
          currentJudgeName={movingDogClassSourceJudge}
          show={show}
          ringOptions={ringOptions}
          onFinish={handleMoveClassToRing}
        />
      case "confirmMerge":
        return (
          <div style={{ padding: "20px" }}>
            <h3 style={{ marginBottom: "12px" }}>Merge All Splits?</h3>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              All split sections will be merged back into a single class. Dogs from every split will be pooled together. This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Button variant="secondary" onClick={() => { setMergingDogClass(null); toggle(); }}>Cancel</Button>
              <Button onClick={() => {
                if (mergingDogClass) mergeSplits(mergingDogClass.jIndex, mergingDogClass.seIndex);
                toggle();
              }}>Merge</Button>
            </div>
          </div>
        );
    }

    return <></>;
  }

  const pModalContent = helperService.usePrevious(modalContent);

  useEffect(() => {
    if (pModalContent !== modalContent) {
      if (modalContent !== "") {
        toggle();
      }
    } else {
      if (!isShown) {
        setModalContent("");
      }
    }
  }, [isShown, modalContent, pModalContent, setModalContent, toggle]);

  const pRingName = helperService.usePrevious(ringName);

  // Commit current judges directly into showRef.current (synchronously) before
  // switching rings. This must happen BEFORE debouncedSave.flush() so that
  // saveJudgeSchedule reads the correct running_order when it fires.
  // Also calls setShow so that the ring reload (setLoaded(false)) reads the
  // correct state from show.rings.
  const commitJudgesToShow = useCallback(() => {
    const latestJudges = judgesRef.current;
    if (!latestJudges.length) return;
    const currentRingId = ringName.id;

    const buildRunningOrder = (jdgs: any[]) => jdgs.map((j: any) => ({
      judge_name: j.judge_name,
      judge_number: j.judge_number,
      dog_classes: (j.dog_classes || []).map((dc: any) => ({
        type: dc.type,
        show_element: dc.show_element,
        level: dc.level,
        section: dc.section,
        entries: dc.entries,
        total_minutes: dc.total_minutes,
        duration: dc.duration,
        dog_run_order: dc.dog_run_order,
        completed_dogs: dc.completed_dogs,
        sex_split: dc.sex_split,
        variation: dc.variation,
        group_with: dc.group_with,
        group_order: dc.group_order,
        label: dc.label,
        name: dc.name,
        split_number: dc.split_number,
        split_total: dc.split_total,
        start_letter: dc.start_letter,
        end_letter: dc.end_letter,
      }))
    }));

    // Update showRef.current synchronously so saveJudgeSchedule reads the right state
    if (showRef.current?.rings) {
      const updatedRings = showRef.current.rings.map((ring: any) => {
        if (ring.ring !== currentRingId) return ring;
        return { ...ring, running_order: buildRunningOrder(latestJudges) };
      });
      showRef.current = { ...showRef.current, rings: updatedRings };
    }

    // Also update React state so the reload reads correct data from show.rings
    setShow((prevShow: any) => ({
      ...prevShow,
      rings: (prevShow.rings || []).map((ring: any) => {
        if (ring.ring !== currentRingId) return ring;
        return { ...ring, running_order: buildRunningOrder(latestJudges) };
      })
    }));
  }, [ringName.id]);

  const handleRingChange = useCallback((e: any, value: IOption) => {
    if (hasUnsavedBreakChanges) {
      saveAllUnsavedBreakChanges();
    }
    // Flush any pending debounced save AND commit judges to show state so
    // the ring reload reads the correct running_order even before the async
    // save completes.
    commitJudgesToShow();
    debouncedSave.flush();
    // Clear judges immediately so the previous ring's tiles don't flash briefly
    // before the new ring finishes loading.
    judgesRingIdRef.current = null;
    setJudges([]);
    setRingName(value);
    setLoaded(false);
  }, [hasUnsavedBreakChanges, saveAllUnsavedBreakChanges, commitJudgesToShow]); // debouncedSave is stable, no need in deps

  useEffect(() => {
    if (ringName !== pRingName && loaded) {
      // Always reload when switching rings
      setLoaded(false);
    }
  }, [ringName, pRingName, loaded]);

  // Flush any pending debounced save when the component unmounts (e.g. ManageShow modal closes).
  // We also commit the latest judges into showRef so the flush reads correct state.
  // debouncedSave is stable (created once, empty deps), so this cleanup fires exactly once.
  useEffect(() => {
    return () => {
      // Sync latest judges into showRef.current so the flush save uses the right data
      const latestJudges = judgesRef.current;
      const currentRingId = ringName.id;
      if (latestJudges.length && showRef.current?.rings) {
        const buildRunningOrder = (jdgs: any[]) => jdgs.map((j: any) => ({
          judge_name: j.judge_name,
          judge_number: j.judge_number,
          dog_classes: (j.dog_classes || []).map((dc: any) => ({
            type: dc.type, show_element: dc.show_element, level: dc.level,
            section: dc.section, entries: dc.entries, total_minutes: dc.total_minutes,
            duration: dc.duration, dog_run_order: dc.dog_run_order,
            completed_dogs: dc.completed_dogs, sex_split: dc.sex_split,
            variation: dc.variation, group_with: dc.group_with,
            group_order: dc.group_order, label: dc.label, name: dc.name,
            split_number: dc.split_number, split_total: dc.split_total,
          }))
        }));
        showRef.current = {
          ...showRef.current,
          rings: showRef.current.rings.map((ring: any) =>
            ring.ring !== currentRingId ? ring : { ...ring, running_order: buildRunningOrder(latestJudges) }
          )
        };
      }
      debouncedSave.flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save unsaved break changes when component unmounts and cleanup timeout
  useEffect(() => {
    const currentTimeout = saveTimeoutRef.current;
    return () => {
      // Clear any pending debounced saves
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      
      // Save any unsaved changes immediately
      if (hasUnsavedBreakChanges) {
        saveAllUnsavedBreakChanges();
      }
    };
  }, [hasUnsavedBreakChanges, saveAllUnsavedBreakChanges]); // Dependencies for cleanup

  // Save unsaved break changes when modal is closed
  useEffect(() => {
    if (!isShown && hasUnsavedBreakChanges) {
      saveAllUnsavedBreakChanges();
    }
  }, [isShown, hasUnsavedBreakChanges, saveAllUnsavedBreakChanges]);

  return (
    <div className={styles.JudgeSchedulesTab}>
      <div className={styles.ControlsBar}>
        <div className={styles.ControlsRow}>
        <div className={styles.RingSelection}>
          <Dropdown className={styles.RingName} value={ringName} label={"Current Ring"} onChange={handleRingChange} options={ringOptions} placeholder="" />
          {ringOptions.length > 1 && <Button onClick={deleteCurrentRing} variant="secondary"><span className={`${styles.Icon} ${styles.Delete}`}></span></Button>}
        </div>
        
        <div className={styles.ActionButtons}>
          {!groupingMode ? (
            <>
              <Button onClick={addRing}>
                Add Ring
              </Button>
              <Button onClick={() => { setModalContent("addRunningOrderJudge"); }}>
                Add Judge
              </Button>
              <Button onClick={resetSchedule}>
                Reset
              </Button>
              {judges.length > 0 && (
                <>
                  <Button onClick={toggleGroupingMode}>
                    Group Levels
                  </Button>
                  <Button onClick={reverseRunningOrder}>
                    Reverse Order
                  </Button>
                </>
              )}
            </>
          ) : (
              <>
                <span className={styles.GroupingModeText}>
                  Select levels to group ({selectedForGrouping.length} selected)
                </span>
                <Button
                  onClick={groupSelectedLevels}
                  disabled={selectedForGrouping.length < 2}
                >
                  Confirm Group
                </Button>
                <Button
                  onClick={toggleGroupingMode}
                  variant="secondary"
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {judges.length > 0 && (
        <div className={styles.FilterOptions}>
          <Checkbox
            id="hideEmptyClasses"
            label="Hide empty elements and levels"
            value={hideEmptyClasses}
            onChange={(e) => {
              setHideEmptyClasses(e.target.checked);
            }}
          />
          <Checkbox
            id="roundStartTimesTo5"
            label="Round start times up to 5 minutes"
            value={!!show.round_start_times_to_5}
            onChange={(e) => {
              const checked = e.target.checked;
              const updatedShow = {
                ...showRef.current,
                round_start_times_to_5: checked,
              };
              showRef.current = updatedShow;
              setShow(updatedShow);
              saveJudgeSchedule(updatedShow, undefined, true);
            }}
          />
        </div>
      )}
      
      {judges.length === 0 && loaded && <div className={styles.NoRunningOrder}>
        <span>No judges have been added to this running order.</span>
      </div>}
      {judges.length > 0 && <DragDropContext
        onDragEnd={onDragEnd}
      >
        <div className={styles.JudgeSchedulesContainer}>
          <div className={styles.JudgeDroppables}>
            {judges.map((j: IRunningOrderDrag, jIndex: number) => {
              return (<Droppable key={jIndex} droppableId={`${jIndex}`}>
                {(provided, snapshot) => (
                  <div ref={provided.innerRef} className={styles.JudgeDroppable} {...provided.droppableProps}>
                    <div className={styles.JudgeInfo}>
                      <div className={styles.JudgeName}>
                        <div className={styles.JudgeNameInner}>{j.judge_name} <span className={styles.judgeNum}>({j.judge_number})</span></div>
                        <div>
                          <ContextMenu
                            label="Actions"
                            disablePortal={true}
                            options={[
                              {
                                label: "Edit Judge",
                                onClick: () => {
                                  setEditingJudge({
                                    index: jIndex,
                                    judge_name: j.judge_name,
                                    judge_number: j.judge_number || "",
                                  });
                                  setModalContent("editRunningOrderJudge");
                                },
                              },
                              {
                                label: "Add Break",
                                onClick: () => { addBreak(jIndex); }
                              },
                              ...(ringOptions.length > 1 ? [{
                                label: "Move Judge to Ring...",
                                onClick:() => {
                                  setMovingJudge(jIndex);
                                  setModalContent("selectRingModal");
                                }
                              }] : []),
                              {
                                label: "Delete Judge",
                                onClick: () => {
                                  // A judge is only considered "assigned" when classes still carry
                                  // active entries or explicit run-order data.
                                  const hasActiveAssignedClasses = (j.dog_classes || []).some((dc: any) => {
                                    if (dc.type === Constants.running_order_dog_class_type.break) return false;
                                    return (dc.entries || 0) > 0 || (dc.dog_run_order || []).length > 0;
                                  });
                                  if (hasActiveAssignedClasses) {
                                    toast.show({
                                      title: "Delete Judge",
                                      content: "This judge still has classes assigned. To swap in a different judge, use Edit Judge on their block — the classes, entries and class setup move with the new judge. Otherwise remove their classes first.",
                                      duration: 5000,
                                      type: "fail"
                                    });
                                    return;
                                  }
                                  
                                  // Confirm deletion
                                  if (window.confirm(`Are you sure you want to delete ${j.judge_name} (${j.judge_number})?`)) {
                                    const _judges = Array.from(judges);
                                    _judges.splice(jIndex, 1);
                                    setJudges(_judges);
                                    // Update showRef synchronously so the debounced save's
                                    // "preserve extra judges" merge doesn't re-add the deleted judge.
                                    showRef.current = {
                                      ...showRef.current,
                                      rings: (showRef.current.rings || []).map((ring: any) =>
                                        ring.ring !== ringName.id ? ring : {
                                          ...ring,
                                          running_order: ring.running_order.filter((ro: any) =>
                                            ro.judge_name !== j.judge_name || (ro.judge_number || '') !== (j.judge_number || '')
                                          )
                                        }
                                      )
                                    };
                                    updateRunningOrder(undefined, _judges);
                                    
                                    toast.show({
                                      title: "Delete Judge",
                                      content: `Successfully deleted ${j.judge_name}`,
                                      duration: 3000,
                                      type: "success"
                                    });
                                  }
                                }
                              }
                            ]}
                          />
                        </div>
                      </div>
                      <div className={styles.JudgeStart}>
                        {jIndex !== 0 && <div className={`${styles.Caret} ${styles.Left}`} onClick={() =>{ moveJudge(jIndex, "left"); }}></div>}
                        <span>Judge {jIndex + 1} ({getJudgeTiming(jIndex)})</span>
                        {(jIndex < judges.length - 1) && <div className={`${styles.Caret} ${styles.Right}`} onClick={() =>{ moveJudge(jIndex, "right"); }}></div>}
                      </div>
                      <div className={styles.JudgeEntries}>
                        <span className={styles.JudgeEntriesInner}>{getJudgeCountsDisplay(j)}</span>
                      </div>
                      <div className={(j.total_hours > 0 && getTotalJudgeHours(j) > j.total_hours) ? `${styles.JudgeTime} ${styles.JudgeTimeOver}` : `${styles.JudgeTime}`}>
                        <div className={styles.JudgeTimeBox}>
                          <div className={styles.JudgeTimeLine}>
                            <span className={styles.JudgeTimeLabel}>{moment(props.show.show_date).format("MMMM Do")} - All Shows/Trials -</span>
                            <span className={styles.JudgeTimeValue}>{getDailyJudgeMinutes(j)}</span>
                          </div>
                          <div className={styles.JudgeTimeLine}>
                            <span className={styles.JudgeTimeLabel}>{moment(props.show.show_date).format("MMMM Do")} - {props.show.show_name} -</span>
                            <span className={styles.JudgeTimeValue}>{getTotalJudgeMinutes(j)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* For UKC Conformation, display breed GROUP tiles - now from dog_classes */}
                    {event.sanctioning_club === Constants.sanctioning_club.UKC && 
                     propShow.show_type === Constants.show_type.conformation &&
                     j.dog_classes.length > 0 ? (
                      <>
                        {j.dog_classes
                          .filter((dogClass: IRunningOrderDogClassDrag) => {
                            // Always show breaks
                            if (dogClass.type === Constants.running_order_dog_class_type.break) return true;
                            // Respect the "Hide empty elements and levels" toggle uniformly
                            return !hideEmptyClasses || (dogClass.entries != null && dogClass.entries > 0);
                          })
                          .map((dogClass: IRunningOrderDogClassDrag, dcIndex: number) => {
                          // Handle breaks in UKC Conformation
                          if (dogClass.type === Constants.running_order_dog_class_type.break) {
                            return (
                              <Draggable key={`${jIndex}-${dcIndex}`} draggableId={`${jIndex}-${dcIndex}`} index={dcIndex}>
                                {(provided, snapshot) => (
                                  <div
                                    className={styles.ElementDraggable}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    {getLevelContent(jIndex, dcIndex, dogClass, j)}
                                  </div>
                                )}
                              </Draggable>
                            );
                          }

                          // Render non-conformation tiles (e.g. Junior Showmanship) in the same UKC tile shell.
                          if (
                            dogClass.show_element !== Constants.dog_class_element.conformation &&
                            dogClass.show_element !== Constants.dog_class_element.conformation_altered
                          ) {
                            const totalEntries = dogClass.entries || 0;
                            const totalMinutes = dogClass.total_minutes || 0;
                            const startTime = getElementTiming(jIndex, dcIndex);

                            return (
                              <Draggable key={`${jIndex}-${dcIndex}`} draggableId={`${jIndex}-${dcIndex}`} index={dcIndex}>
                                {(provided, snapshot) => (
                                  <div
                                    className={styles.ElementDraggable}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <div className={styles.ElementIconWrap}>
                                      <div className={`${styles.icon} ${styles.handle}`}></div>
                                    </div>
                                    <div className={styles.ElementInfo}>
                                      <div className={styles.ElementName}>
                                        <strong>{dogClass.level || dogClass.show_element}</strong> ({totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}, ~{Math.round(totalMinutes)} mins)
                                      </div>
                                      <div className={styles.startTime}>Est. Start Time: {startTime}</div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          }
                          
                          const breedGroup = dogClass.level; // Breed group is stored in level
                          
                          // Get counts for this breed group from the counts map
                          const groupCounts = {
                            male_count: 0, female_count: 0, champion_count: 0, grand_champion_count: 0,
                            novice_puppy_count: 0, brace_count: 0, veteran_count: 0,
                            alt_male_count: 0, alt_female_count: 0, alt_champion_count: 0, alt_grand_champion_count: 0,
                            alt_novice_puppy_count: 0, alt_brace_count: 0, alt_veteran_count: 0,
                          };
                          
                          ukcConformationCounts.forEach((counts, key) => {
                            const [keyBreedGroup] = key.split('-');
                            if (keyBreedGroup === breedGroup) {
                              // When this tile has an alpha-split, only count breeds in range
                              if (dogClass.start_letter && dogClass.end_letter) {
                                const breed = key.substring(keyBreedGroup.length + 1);
                                const first = breed[0]?.toUpperCase() ?? 'A';
                                if (first < dogClass.start_letter || first > dogClass.end_letter) return;
                              }
                              // Aggregate all counts for this breed group
                              groupCounts.male_count += counts.male_count || 0;
                              groupCounts.female_count += counts.female_count || 0;
                              groupCounts.champion_count += counts.champion_count || 0;
                              groupCounts.grand_champion_count += counts.grand_champion_count || 0;
                              groupCounts.novice_puppy_count += counts.novice_puppy_count || 0;
                              groupCounts.brace_count += counts.brace_count || 0;
                              groupCounts.veteran_count += counts.veteran_count || 0;
                              groupCounts.alt_male_count += counts.alt_male_count || 0;
                              groupCounts.alt_female_count += counts.alt_female_count || 0;
                              groupCounts.alt_champion_count += counts.alt_champion_count || 0;
                              groupCounts.alt_grand_champion_count += counts.alt_grand_champion_count || 0;
                              groupCounts.alt_novice_puppy_count += counts.alt_novice_puppy_count || 0;
                              groupCounts.alt_brace_count += counts.alt_brace_count || 0;
                              groupCounts.alt_veteran_count += counts.alt_veteran_count || 0;
                            }
                          });
                          
                          // Calculate total entries from groupCounts - SINGLE SOURCE OF TRUTH
                          const totalEntries = groupCounts.male_count + groupCounts.female_count + 
                                              groupCounts.champion_count + groupCounts.grand_champion_count + 
                                              groupCounts.novice_puppy_count + groupCounts.brace_count + 
                                              groupCounts.veteran_count + groupCounts.alt_male_count + 
                                              groupCounts.alt_female_count + groupCounts.alt_champion_count + 
                                              groupCounts.alt_grand_champion_count + groupCounts.alt_novice_puppy_count + 
                                              groupCounts.alt_brace_count + groupCounts.alt_veteran_count;
                          const totalMinutes = dogClass.total_minutes || 0;
                          const startTime = getElementTiming(jIndex, dcIndex);
                          
                          return (
                            <Draggable key={`${jIndex}-${dcIndex}`} draggableId={`${jIndex}-${dcIndex}`} index={dcIndex}>
                              {(provided, snapshot) => (
                                <div
                                  className={styles.ElementDraggable}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <div className={styles.ElementIconWrap}>
                                    <div className={`${styles.icon} ${styles.handle}`}></div>
                                  </div>
                                  <div className={styles.ElementInfo} style={{ flex: 1 }}>
                                    <div className={styles.ElementName}><strong>{breedGroup}{dogClass.start_letter && dogClass.end_letter ? ` (${dogClass.start_letter}–${dogClass.end_letter})` : ''}</strong> ({totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}, ~{Math.round(totalMinutes)} mins)</div>
                                    <div className={styles.startTime}>Est. Start Time: {startTime}</div>
                                    <br />
                                    <div className={styles.ElementDetails}>
                                      <div><strong>Regular: </strong>{groupCounts.male_count}-{groupCounts.female_count}-{groupCounts.champion_count}-{groupCounts.grand_champion_count} <strong>NP-</strong>{groupCounts.novice_puppy_count} <strong>BR-</strong>{groupCounts.brace_count} <strong>VET-</strong>{groupCounts.veteran_count}</div>
                                      <div><strong>Altered: </strong>{groupCounts.alt_male_count}-{groupCounts.alt_female_count}-{groupCounts.alt_champion_count}-{groupCounts.alt_grand_champion_count} <strong>NP-</strong>{groupCounts.alt_novice_puppy_count} <strong>BR-</strong>{groupCounts.alt_brace_count} <strong>VET-</strong>{groupCounts.alt_veteran_count}</div>
                                    </div>
                                  </div>
                                  {ringOptions.length > 1 && (
                                    <div style={{ alignSelf: "center" }}>
                                      <ContextMenu
                                        label="Edit"
                                        disablePortal={true}
                                        options={[
                                          {
                                            label: "Move to Ring...",
                                            onClick: () => {
                                              setMovingDogClass(dogClass);
                                              setMovingDogClassSourceJudge(j.judge_name);
                                              setModalContent("moveClassToRing");
                                            },
                                          },
                                        ]}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                      </>
                    ) : (
                      /* Standard display for other show types */
                      j.dog_classes
                        .filter((se: IRunningOrderDogClassDrag) => {
                          // Always show breaks
                          if (se.type === Constants.running_order_dog_class_type.break) {
                            return true;
                          }
                          
                          // NEVER show placeholder classes (those without a level)
                          // These are used for grouping structure but shouldn't be rendered
                          if (!se.level) return false;

                          if (!isScentworkClassOffered(propShow, se)) return false;
                          
                          // Respect the "Hide empty elements and levels" toggle uniformly.
                          // For AKC Conformation specialty non-split classes (e.g. Brace, Brood Bitch, Stud Dog,
                          // Best of Breed), use live specialty counts instead of the stored entries field.
                          // The stored entries may be 0/stale if the sex-split useEffect didn't run this session
                          // (e.g. all regular classes were already split from a prior session), while
                          // getElementCountsDisplay correctly reads from akcSpecialtyCounts.
                          if (isSpecialtyShow && !se.sex_split && akcSpecialtyCounts.size > 0) {
                            const key = `${j.judge_name}-${se.level}`;
                            const counts = akcSpecialtyCounts.get(key);
                            const liveCount = counts ? (counts.males || 0) + (counts.females || 0) : (se.entries || 0);
                            return !hideEmptyClasses || liveCount > 0;
                          }
                          return !hideEmptyClasses || (se.entries && se.entries > 0);
                        })
                        .map((se: IRunningOrderDogClassDrag, filteredIndex: number) => {
                      // Get the actual index in the original dog_classes array
                      const seIndex = j.dog_classes.findIndex((dc: IRunningOrderDogClassDrag) => dc.index === se.index);
                      const isDisabled = se.group_with !== undefined || groupingMode;
                      const levelKey = `${se.show_element}-${se.level}`;
                      const selectionKey = `${jIndex}-${seIndex}-${levelKey}`;
                      const isSelected = selectedForGrouping.includes(selectionKey);
                      
                      // Determine if this class should be hidden
                      // Classes with group_with should be hidden (they're grouped into another class)
                      // OLD format: child has group_with pointing to parent name (e.g., "Open B")
                      // NEW format: parent has group_with pointing to child identifier (e.g., "Obedience:Preferred Open")
                      //             and child has no group_with but is pointed to by parent
                      
                      const parentIdentifier = `${se.show_element}:${se.level}`;
                      
                      // Check if this class is a child in NEW format (no group_with, but another class points to it with ":" format)
                      const isChildInNewFormat = se.group_with === undefined && j.dog_classes.some((dc: IRunningOrderDogClassDrag) => 
                        dc.group_with === parentIdentifier && dc.group_with.includes(':')
                      );
                      
                      // Check if this class has group_with (OLD or NEW format parent)
                      const hasGroupWith = se.group_with !== undefined;
                      
                      // Hide if:
                      // 1. Has group_with (OLD format child OR NEW format parent with children)
                      // 2. Is a child in NEW format (another class points to it)
                      const isHiddenChild = hasGroupWith || isChildInNewFormat;
                      
                      // Use filteredIndex for Draggable (sequential 0,1,2,3... for visible items)
                      // Use seIndex (actual array index) for operations like removeBreak
                      return (<Draggable isDragDisabled={isDisabled} key={se.index} draggableId={se.index.toString()} index={filteredIndex}>
                        {(provided, snapshot) => (
                          <div
                            className={`
                              ${isHiddenChild ? styles.ElementDraggableHidden : styles.ElementDraggable}
                              ${groupingMode ? styles.GroupingMode : ''}
                              ${isSelected ? styles.Selected : ''}
                              ${isLevelGrouped(se) ? styles.Grouped : ''}
                            `}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={groupingMode ? (e) => {
                              // In grouping mode, clicking anywhere on the element toggles selection
                              if (se.type !== Constants.running_order_dog_class_type.break) {
                                e.preventDefault();
                                toggleLevelSelection(jIndex, seIndex, se);
                              }
                            } : undefined}
                          >
                            {getLevelContent(jIndex, seIndex, se, j)}
                            {se.type === "dog_class" && <div style={{ alignSelf: "center" }}>
                              <ContextMenu
                                label="Edit"
                                disablePortal={true}
                                options={[
                                  {
                                    label: "Edit Class",
                                    onClick: () => {
                                      setSelectedDogClass(se);
                                      setSelectedJudgeName(j.judge_name);
                                      setModalContent("editRODogClass");
                                    },
                                  },
                                  ...(ringOptions.length > 1 ? [{
                                    label: "Move to Ring...",
                                    onClick: () => {
                                      setMovingDogClass(se);
                                      setMovingDogClassSourceJudge(j.judge_name);
                                      setModalContent("moveClassToRing");
                                    },
                                  }] : []),
                                  ...(!se.group_with && (se.split_total === undefined || se.split_total < 4) ? [{
                                    label: "Split ↔",
                                    onClick: () => splitDogClass(jIndex, seIndex),
                                  }] : []),
                                  ...(se.split_number !== undefined ? [{
                                    label: "Merge —",
                                    onClick: () => {
                                      if (se.dog_run_order && se.dog_run_order.length > 0) {
                                        setMergingDogClass({ se, jIndex, seIndex });
                                        setModalContent("confirmMerge");
                                        toggle();
                                      } else {
                                        mergeSplits(jIndex, seIndex);
                                      }
                                    },
                                  }] : []),
                                ]}
                              />
                            </div>}
                          </div>
                        )}
                      </Draggable>);
                    }))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>);
            })}
          </div>
        </div>
      </DragDropContext>}
      <Modal
        className={modalContent}
        isShown={isShown}
        hide={toggle}
        modalContent={getModalContent()}
      />
    </div>
  );
};

export default JudgeSchedulesTab;